import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Calendar, Users, Settings, LogOut, ChevronLeft, ChevronRight, Phone, Check, X, Clock, Mail, UtensilsCrossed, Edit, CalendarIcon, ChevronDown, ChevronUp, Trash2, Pause, Play, RefreshCw, Download, MoreVertical, Receipt, Bell, MessageSquare, MessageSquarePlus, CheckCheck, ArrowRightLeft, Building, AlertTriangle, Sparkles, Crown, BarChart3, ShieldAlert, LayoutGrid, LayoutList, Eye, EyeOff, Printer, StickyNote, Baby, UserX, BookOpen, Ban, type LucideIcon } from "lucide-react";
import { CompactTableStatusView } from "@/components/CompactTableStatusView";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDashboardChromeState } from "@/hooks/useDashboardChromeState";
import { DashboardNavRail, type DashboardTabValue } from "@/components/dashboard/DashboardNavRail";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DashboardBottomTabs } from "@/components/dashboard/DashboardBottomTabs";
import { useState, useEffect, useMemo } from "react";
import { format, addDays, subDays, isToday, isTomorrow, isYesterday, differenceInMinutes, parse } from "date-fns";
import { fr } from "date-fns/locale";
import { ClientsView } from "@/components/ClientsView";
import { ReservationSearch } from "@/components/ReservationSearch";
import { FloorPlanView } from "@/components/FloorPlanView";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { supabase } from "@/integrations/supabase/client";
import { isTableAvailableOnDate } from "@/lib/tableAvailabilityUtils";
import { logger } from "@/lib/logger";
import { getBlockedTableIds } from "@/lib/reservationConflictUtils";
import { isPhysicalOverlapError, getConflictToastMessage } from "@/lib/reservationConflictError";
import { Reservation } from "@/types/table";
import { hasInactiveOrMissingTable, hasCapacityExceeded, hasClosureConflict } from "@/lib/reservationProtectionUtils";
import { useEstablishmentClosures } from "@/hooks/useEstablishmentClosures";
import { useTimeSlotClosures } from "@/hooks/useTimeSlotClosures";
import { useTerraceClosures } from "@/hooks/useTerraceClosures";

import { EditReservationDialog } from "@/components/EditReservationDialog";
import { ReservationPreviewDialog } from "@/components/ReservationPreviewDialog";
import { QuickTableChangeDialog } from "@/components/QuickTableChangeDialog";
import { ManualReplyDialog } from "@/components/ManualReplyDialog";
import { getClientLoyaltyLevel } from "@/lib/clientUtils";
import { useClientsWithNotes } from "@/hooks/useClientsWithNotes";
import { CustomerData } from "@/types/client";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { getTimeSlotsForDate } from "@/lib/timeSlotUtils";
import { getStaffSlotsForDate } from "@/lib/zoneScheduleUtils";
import { useZoneTimeWindows } from "@/hooks/useZoneTimeWindows";
import { useBookableZones } from "@/hooks/useBookableZones";
import { toast } from "@/hooks/use-toast";
import { exportReservationsToCSV } from "@/lib/csvExportUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useReservationSettings } from "@/hooks/useReservationSettings";
import { PauseReservationsDialog } from "@/components/PauseReservationsDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useEstablishmentSettings } from "@/hooks/useEstablishmentSettings";
import { PlanBadge } from "@/components/PlanBadge";
import { AIChatDialog } from "@/components/ai/AIChatDialog";
import { OnboardingWidget } from "@/components/onboarding/OnboardingWidget";
import { EstablishmentSelector } from "@/components/EstablishmentSelector";
import { ManagerNotificationDialog } from "@/components/notifications/ManagerNotificationDialog";
import { SpamAlertBanner } from "@/components/SpamAlertBanner";
import { OnlineClosureDialog } from "@/components/OnlineClosureDialog";
import { DayServiceSummary } from "@/components/DayServiceSummary";
import { computeServiceCounts } from "@/lib/serviceCounts";
import { TrialHelpBanner } from "@/components/TrialHelpBanner";
import { useConfigurationCheck } from "@/hooks/useConfigurationCheck";
import { PrintReservationsDialog } from "@/components/PrintReservationsDialog";
import { WhatsNewPopover } from "@/components/WhatsNewPopover";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import { DashboardListView } from "@/components/DashboardListView";
import { DashboardTimelineView } from "@/components/DashboardTimelineView";
import { List, GanttChart, Smartphone, CalendarX } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { PWAInstallDialog } from "@/components/PWAInstallDialog";
import { PiktableLoader } from "@/components/PiktableLoader";
import { TerraceManagementTab } from "@/components/TerraceManagementTab";
import { WalkInDialog } from "@/components/WalkInDialog";
import { AvailabilityDialog } from "@/components/AvailabilityDialog";
import { NewReservationDialog } from "@/components/NewReservationDialog";
import { QuickReservationDialog } from "@/components/QuickReservationDialog";
import { WaitlistBanner } from "@/components/WaitlistBanner";
import { TerraceReplaceBanner } from "@/components/TerraceReplaceBanner";
import { WaitlistAssignTableDialog } from "@/components/WaitlistAssignTableDialog";
import { UserPlus } from "lucide-react";
import { KeyRound } from "lucide-react";
import { ChangePasswordDialog } from "@/components/auth/ChangePasswordDialog";

type DashboardMenuAction = {
  id: string;
  label: string;
  icon: LucideIcon;
  onSelect: () => void | Promise<void>;
  visible?: boolean;
  phoneOnly?: boolean;
  iconClassName?: string;
};

type DashboardMenuSection = {
  id: string;
  label: string;
  actions: DashboardMenuAction[];
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { signOut, role, establishmentId, isAdmin, isSuperAdmin, overrideEstablishmentId, setOverrideEstablishmentId, dashboardLastSeenAt, updateDashboardLastSeen, session } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [onlineClosureOpen, setOnlineClosureOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const { 
    tables: supabaseTables, 
    combinedTables: supabaseCombinedTables, 
    reservations: supabaseReservations,
    availabilities: supabaseAvailabilities,
    weekdayAvailabilities: supabaseWeekdayAvailabilities,
    bookablePeriods: supabaseBookablePeriods,
    timeSlots: supabaseTimeSlots,
    dateOverrides: supabaseDateOverrides,
    loading, 
    error,
    refetch
  } = useSupabaseData({ useAuthenticatedData: true, establishmentId: establishmentId || undefined, session, selectedDate });
  
  const isMobile = useIsMobile();
  const {
    navRailCollapsed,
    toggleNavRail,
    summaryCollapsed,
    setSummaryCollapsed,
    toggleSummary,
  } = useDashboardChromeState();
  const [currentTime, setCurrentTime] = useState(new Date());


  // lastLoginDate replaced by dashboardLastSeenAt from AuthContext (DB-backed)
  const [editingReservation, setEditingReservation] = useState<typeof reservations[0] | null>(null);
  const [previewReservation, setPreviewReservation] = useState<typeof reservations[0] | null>(null);
  const [floorPlanCreate, setFloorPlanCreate] = useState<{ tableId: string; time: string; date: Date } | null>(null);
  const [collapsedSlots, setCollapsedSlots] = useState<Set<string>>(new Set());
  const [collapsedTableStatus, setCollapsedTableStatus] = useState<Set<string>>(new Set());
  
  // Compact table view - auto-enable when more than 12 tables total
  const totalTablesCount = supabaseTables.filter(t => t.isActive).length + supabaseCombinedTables.filter(ct => ct.isActive).length;
  const [compactTableView, setCompactTableView] = useState<boolean>(() => {
    const stored = localStorage.getItem('dashboardCompactTableView');
    return stored !== null ? stored === 'true' : true; // Default to compact
  });
  
  // Persist compact view preference
  useEffect(() => {
    localStorage.setItem('dashboardCompactTableView', String(compactTableView));
  }, [compactTableView]);
  
  

  // Hide table status section - persisted preference
  const [hideTableStatus, setHideTableStatus] = useState<boolean>(() => {
    const stored = localStorage.getItem('dashboardHideTableStatus');
    return stored === 'true';
  });
  
  // Persist hide table status preference
  useEffect(() => {
    localStorage.setItem('dashboardHideTableStatus', String(hideTableStatus));
  }, [hideTableStatus]);

  // Hide cancelled reservations - persisted preference (default: shown)
  const [hideCanceled, setHideCanceled] = useState<boolean>(() => {
    return localStorage.getItem('dashboardHideCanceled') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('dashboardHideCanceled', String(hideCanceled));
  }, [hideCanceled]);

  // View mode: grouped (default), list, or timeline
  const [viewMode, setViewMode] = useState<"grouped" | "list" | "timeline">(() => {
    const stored = localStorage.getItem('dashboardViewMode');
    if (stored === 'list' || stored === 'timeline') return stored;
    return 'grouped';
  });

  // Persist view mode preference
  useEffect(() => {
    localStorage.setItem('dashboardViewMode', viewMode);
  }, [viewMode]);
  
  const [targetReservationId, setTargetReservationId] = useState<number | null>(null);
  
  const [sendingEmail, setSendingEmail] = useState<number | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailToSend, setEmailToSend] = useState<{ reservationId: number; reservationUuid: string; emailType: 'cancellation' | 'modification' | 'confirmation'; reservationName: string; reservationEmail: string } | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [newReservationForEmail, setNewReservationForEmail] = useState<{
    reservationId: number;
    reservationUuid: string;
    customerName: string;
    customerEmail: string;
  } | null>(null);
  const [showEmailConfirmDialog, setShowEmailConfirmDialog] = useState(false);
  const [manualReplyTarget, setManualReplyTarget] = useState<import("@/components/ManualReplyDialog").ManualReplyTarget | null>(null);
  const [manualReplyOpen, setManualReplyOpen] = useState(false);


  

  const [showInactiveDialog, setShowInactiveDialog] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam === 'clients' || tabParam === 'recherche' || tabParam === 'salle' || tabParam === 'terrasse' ? tabParam : 'reservations';
  });

  // Sync activeTab with URL params
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const newTab = tabParam === 'clients' || tabParam === 'recherche' || tabParam === 'salle' || tabParam === 'terrasse' ? tabParam : 'reservations';
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams]);

  // Scroll to targeted reservation when clicking "Voir la réservation"
  useEffect(() => {
    if (targetReservationId !== null) {
      const timer = setTimeout(() => {
        const element = document.getElementById(`reservation-${targetReservationId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add temporary highlight effect
          element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
          }, 2000);
        }
        setTargetReservationId(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [targetReservationId, selectedDate]);

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };
  
  const { settings: reservationSettings, loading: settingsLoading, updatePauseWithMessage } = useReservationSettings({ establishmentId: establishmentId || undefined });
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [retroactiveDialogOpen, setRetroactiveDialogOpen] = useState(false);
  const [forceOverrideDialogOpen, setForceOverrideDialogOpen] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [waitlistAssigning, setWaitlistAssigning] = useState<typeof reservations[0] | null>(null);
  const { isInstalled, isIOS, canPromptInstall, promptInstall } = usePWAInstall();
  const [showPWADialog, setShowPWADialog] = useState(false);
  const { settings: establishmentSettings, establishmentName } = useEstablishmentSettings(establishmentId);
  const { closures } = useEstablishmentClosures({ establishmentId: establishmentId || undefined });
  const { isSlotClosedOnDate } = useTimeSlotClosures({ establishmentId: establishmentId || undefined });
  const { isTerraceClosedOnDate } = useTerraceClosures({ establishmentId: establishmentId || undefined });
  const { status: configStatus, shouldShowWidget: showOnboardingWidget, dismissWidget } = useConfigurationCheck(establishmentId);
  const { zoneWindows } = useZoneTimeWindows({ establishmentId: establishmentId || undefined, useAuthenticatedData: true });
  const { zones: dashboardZones } = useBookableZones({ establishmentId: establishmentId || undefined, useAuthenticatedData: true });
  const dashboardActiveZoneIds = useMemo(
    () => new Set(dashboardZones.filter(z => z.isActive).map(z => z.id)),
    [dashboardZones]
  );
  const [reservations, setReservations] = useState<Array<{
    id: number;
    supabaseId: string;
    customerId?: string;
    time: string;
    name: string;
    persons: number;
    numberOfChildren: number;
    hasStroller?: boolean;
    durationMinutes?: number;
    tableId: string | undefined;
    combinedTableId: string | undefined;
    status: "planifié" | "arrivé" | "annulé" | "en_attente";
    cancellationReason?: string;
    phone: string;
    email: string | undefined;
    reservationDate: Date;
    createdAt: Date;
    notes: string | undefined;
    internalNotes: string | undefined;
    customAnswers?: { question: string; answers: string[] };
    source?: string;
    isLocked?: boolean;
  }>>([]);

  // Convert dashboard reservations to Reservation type for ClientsView
  const convertedReservations: Reservation[] = reservations.map(r => ({
    id: r.id.toString(),
    supabaseId: r.supabaseId,
    tableId: r.tableId,
    combinedTableId: r.combinedTableId,
    customerFirstName: r.name.split(' ')[0] || '',
    customerLastName: r.name.split(' ').slice(1).join(' ') || '',
    customerEmail: r.email || '',
    customerPhone: r.phone,
    numberOfPeople: r.persons,
    numberOfChildren: r.numberOfChildren,
    date: r.reservationDate,
    startTime: r.time,
    status: r.status,
    durationMinutes: r.durationMinutes,
    createdAt: r.createdAt,
    isLocked: r.isLocked,
  }));

  // Sync Supabase reservations to local state
  useEffect(() => {
    setReservations(supabaseReservations.map((r, index) => ({
      id: index + 1, // Local numeric ID for UI purposes
      supabaseId: r.id, // UUID Supabase pour l'envoi d'emails
      customerId: r.customerId,
      time: r.startTime.substring(0, 5), // Convert "09:00:00" to "09:00"
      name: [r.customerFirstName, r.customerLastName].filter(Boolean).join(' ') || 'Client',
      persons: r.numberOfPeople,
      numberOfChildren: r.numberOfChildren || 0,
      hasStroller: r.hasStroller || false,
      durationMinutes: r.durationMinutes,
      tableId: r.tableId,
      combinedTableId: r.combinedTableId,
      status: r.status,
      cancellationReason: r.cancellationReason,
      phone: r.customerPhone,
      email: r.customerEmail,
      reservationDate: r.date,
      createdAt: r.createdAt,
      notes: r.notes,
      internalNotes: r.internalNotes,
      customAnswers: r.customAnswers,
      source: r.source,
      terracePreference: r.terracePreference,
      isLocked: r.isLocked,
    })));
  }, [supabaseReservations]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  // Show warning dialog if establishment is inactive
  useEffect(() => {
    if (establishmentSettings && establishmentSettings.isActive === false) {
      setShowInactiveDialog(true);
    }
  }, [establishmentSettings]);

  const handleUpdateReservationStatus = async (reservationId: number, status: "arrivé" | "annulé" | "planifié" | "en_attente", cancellationReason?: string) => {
    // Sauvegarder l'ancien état pour rollback
    const previousReservations = [...reservations];

    // Mise à jour optimiste immédiate
    setReservations(prev => prev.map(r => 
      r.id === reservationId ? { ...r, status, cancellationReason: status === 'annulé' ? cancellationReason : undefined } : r
    ));

    try {
      const dashboardReservation = previousReservations.find(r => r.id === reservationId);
      if (!dashboardReservation) {
        throw new Error('Réservation introuvable');
      }
      const supabaseReservation = supabaseReservations.find(r => r.id === dashboardReservation.supabaseId);
      if (!supabaseReservation) {
        throw new Error('Réservation Supabase introuvable');
      }

      const body: Record<string, any> = { 
        reservationId: supabaseReservation.id,
        status 
      };
      if (cancellationReason) {
        body.cancellationReason = cancellationReason;
      }

      const { data, error } = await supabase.functions.invoke(
        'update-reservation-status',
        { body }
      );

      // Gérer les erreurs de conflit spécifiques
      if (error) {
        const errorMessage = error.message || '';
        
        if (errorMessage.includes('TABLE_OCCUPIED') || 
            errorMessage.includes('COMBINED_TABLE_OCCUPIED') || 
            errorMessage.includes('TABLE_IN_COMBINATION_OCCUPIED')) {
          setReservations(previousReservations);
          toast({
            title: "Conflit de réservation",
            description: "Cette table est déjà occupée pour ce créneau. Veuillez d'abord changer la table de cette réservation avant de modifier son statut.",
            variant: "destructive",
          });
          return;
        }
        
        throw error;
      }

      // Toast de confirmation
      const statusLabels: Record<string, string> = {
        'arrivé': 'Client marqué comme arrivé',
        'annulé': cancellationReason === 'no-show' ? 'Réservation marquée no-show' : 'Réservation annulée',
        'planifié': 'Réservation replanifiée'
      };

      toast({
        title: "Statut mis à jour",
        description: statusLabels[status],
      });

      // Rafraîchir en arrière-plan sans bloquer l'UI
      refetch();

    } catch (error) {
      // Rollback en cas d'erreur
      setReservations(previousReservations);
      logger.error('Erreur changement statut:', error);
      toast({
        title: "Erreur",
        description: "Impossible de changer le statut de la réservation.",
        variant: "destructive",
      });
    }
  };

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetch(),
        refetchClients(),
      ]);
      toast({
        title: "Données actualisées",
        description: "Les réservations ont été rafraîchies.",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de rafraîchir les données.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };


  // Check if a time slot has passed (based on end time)
  const isSlotPassed = (slotEnd: string): boolean => {
    if (!isToday(selectedDate)) return false;
    
    const [hours, minutes] = slotEnd.split(':').map(Number);
    const slotEndTime = new Date();
    slotEndTime.setHours(hours, minutes, 0, 0);
    
    return currentTime > slotEndTime;
  };

  const toggleSlotCollapse = (slotStart: string, slotEnd?: string) => {
    const slotPassed = slotEnd ? isSlotPassed(slotEnd) : false;
    
    setCollapsedSlots(prev => {
      const newSet = new Set(prev);
      
      if (slotPassed) {
        // For passed slots: toggle the "expanded" marker
        const expandedKey = `expanded_${slotStart}`;
        if (newSet.has(expandedKey)) {
          newSet.delete(expandedKey);
        } else {
          newSet.add(expandedKey);
        }
      } else {
        // For current/future slots: normal toggle
        if (newSet.has(slotStart)) {
          newSet.delete(slotStart);
        } else {
          newSet.add(slotStart);
        }
      }
      return newSet;
    });
  };

  const toggleTableStatusCollapse = (slotStart: string) => {
    setCollapsedTableStatus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(slotStart)) {
        newSet.delete(slotStart);
      } else {
        newSet.add(slotStart);
      }
      return newSet;
    });
  };

  const handleCreateReservation = async (newReservation: {
    customerFirstName: string;
    customerLastName: string;
    customerEmail?: string;
    customerPhone?: string;
    numberOfPeople: number;
    numberOfChildren?: number;
    hasStroller?: boolean;
    terracePreference?: string;
    tableId?: string;
    combinedTableId?: string;
    startTime: string;
    date: Date;
    notes?: string;
    dietaryPreferences?: string[];
    internalNotes?: string;
    durationMinutes?: number;
    source?: string;
    customAnswers?: { question: string; answers: string[] };
    status?: string;
    zonePreference?: string[];
    isRetroactive?: boolean;
    skipEmails?: boolean;
    skipTokens?: boolean;
    forceOverride?: boolean;
  }) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-reservation', {
        body: {
          establishmentId,
          customer: {
            email: newReservation.customerEmail || null,
            firstName: newReservation.customerFirstName,
            lastName: newReservation.customerLastName,
            phone: newReservation.customerPhone || null,
          },
          reservation: {
            date: formatDateLocal(newReservation.date),
            startTime: newReservation.startTime,
            tableId: newReservation.tableId || null,
            combinedTableId: newReservation.combinedTableId || null,
            numberOfPeople: newReservation.numberOfPeople,
            numberOfChildren: newReservation.numberOfChildren || 0,
            hasStroller: newReservation.hasStroller ?? false,
            terracePreference: newReservation.terracePreference || 'no_preference',
            zonePreference: Array.isArray(newReservation.zonePreference) && newReservation.zonePreference.length > 0 ? newReservation.zonePreference : undefined,
            notes: newReservation.notes,
            internalNotes: newReservation.internalNotes || null,
            dietaryPreferences: newReservation.dietaryPreferences,
            durationMinutes: newReservation.durationMinutes || null,
            source: newReservation.source || null,
            customAnswers: newReservation.customAnswers?.answers?.length
              ? newReservation.customAnswers
              : undefined,
            status: newReservation.status || null,
          },
          sendEmail: false,
          skipEmails: newReservation.skipEmails ?? false,
          skipTokens: newReservation.skipTokens ?? false,
          forceOverride: newReservation.forceOverride ?? false,
        }
      });

      if (error) throw error;

      // Récupérer les infos de la réservation créée pour l'email
      const createdReservation = data?.reservation;

      // Si le client a un email valide, proposer l'envoi
      // ⚠️ En mode rétroactif : ne jamais proposer l'envoi (réservation historique)
      if (newReservation.customerEmail && !newReservation.isRetroactive && !newReservation.forceOverride) {
        setNewReservationForEmail({
          reservationId: data?.reservationNumber || 0,
          reservationUuid: createdReservation?.id || '',
          customerName: [newReservation.customerFirstName, newReservation.customerLastName].filter(Boolean).join(' ') || 'Client',
          customerEmail: newReservation.customerEmail
        });
        setShowEmailConfirmDialog(true);
      }

      toast({
        title: newReservation.isRetroactive
          ? "Réservation rétroactive enregistrée"
          : newReservation.forceOverride
            ? "Réservation créée (mode manager override)"
            : "Réservation créée",
        description: newReservation.isRetroactive
          ? "Enregistrée avec le statut « arrivé ». Aucun email envoyé."
          : newReservation.forceOverride
            ? "Créée en forçant les vérifications de fermeture. Aucun email envoyé."
            : "La réservation a été enregistrée avec succès.",
      });

      // Rafraîchir les données
      refetch();
    } catch (error) {
      logger.error('Erreur création réservation:', error);
      
      // Extraire le message d'erreur de la réponse
      let errorMessage = "Impossible de créer la réservation.";
      
      if (error && typeof error === 'object') {
        // Si c'est une FunctionsHttpError de Supabase, context est un objet Response
        if ('context' in error && error.context) {
          try {
            const context = error.context as any;
            // FunctionsHttpError.context is a Response object — use .json()
            if (typeof context.json === 'function') {
              const body = await context.json();
              if (body?.error) {
                errorMessage = body.error;
              }
            } else if (context.body) {
              const body = typeof context.body === 'string' ? JSON.parse(context.body) : context.body;
              if (body?.error) {
                errorMessage = body.error;
              }
            }
          } catch (parseError) {
            // Fallback au message d'erreur standard
          }
        }
        // Fallback: message générique seulement si pas déjà extrait
        if (errorMessage === "Impossible de créer la réservation." && 'message' in error && typeof error.message === 'string' && !error.message.includes('non-2xx')) {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Erreur de réservation",
        description: errorMessage,
        variant: "destructive",
      });
      
      // Re-throw pour que l'AvailabilityDialog ne se ferme pas en cas d'échec
      throw error;
    }
  };

  const handleUpdateReservation = async (reservationId: number, updates: {
    customerName: string;
    customerEmail?: string;
    customerPhone: string;
    numberOfPeople: number;
    numberOfChildren?: number;
    hasStroller?: boolean;
    terracePreference?: string;
    tableId?: string;
    combinedTableId?: string;
    date: Date;
    startTime: string;
    notes?: string;
    internalNotes?: string;
    status?: "planifié" | "arrivé" | "annulé" | "en_attente";
    durationMinutes?: number;
    source?: string;
    customAnswers?: { question: string; answers: string[] } | null;
    _supabaseId?: string;
  }) => {
    logger.log('🔄 Mise à jour réservation:', {
      reservationId,
      _supabaseId: updates._supabaseId,
      tableId: updates.tableId,
      combinedTableId: updates.combinedTableId,
      updates
    });
    try {
      // Utiliser le supabaseId direct si fourni, sinon fallback sur l'ancien mécanisme
      let supabaseUuid: string;
      if (updates._supabaseId) {
        supabaseUuid = updates._supabaseId;
      } else {
        const dashboardReservation = reservations.find(r => r.id === reservationId);
        if (!dashboardReservation) {
          throw new Error('Réservation introuvable');
        }
        supabaseUuid = dashboardReservation.supabaseId;
      }

      // Séparer le nom en prénom et nom
      const [firstName, ...lastNameParts] = updates.customerName.split(' ');
      const lastName = lastNameParts.join(' ');

      // Mettre à jour dans Supabase
      // Build update object dynamically to avoid overwriting notes with undefined
      const updateData: Record<string, any> = {
        number_of_people: updates.numberOfPeople,
        number_of_children: updates.numberOfChildren ?? 0,
        has_stroller: updates.hasStroller ?? false,
        terrace_preference: updates.terracePreference || 'no_preference',
        date: formatDateLocal(updates.date),
        start_time: updates.startTime,
        table_id: updates.tableId || null,
        combined_table_id: updates.combinedTableId || null,
        status: updates.status,
        duration_minutes: updates.durationMinutes || null,
        source: updates.source || null,
      };

      // Only include notes fields if explicitly provided (not undefined)
      if (updates.notes !== undefined) {
        updateData.notes = updates.notes || null;
      }
      if (updates.internalNotes !== undefined) {
        updateData.internal_notes = updates.internalNotes || null;
      }
      // Réponse à la question de l'établissement : n'écrase la valeur existante
      // que si une question est applicable (champ affiché côté équipe).
      if (updates.customAnswers !== undefined) {
        updateData.custom_answers = updates.customAnswers;
      }

      const { error } = await supabase
        .from('reservations')
        .update(updateData)
        .eq('id', supabaseUuid);

      if (error) throw error;

      // Detach from shared walk-in customer if name changed
      const originalReservation = reservations.find(r => r.id === reservationId);
      const isSharedWalkIn = originalReservation
        && originalReservation.name === 'Walk-in'
        && !originalReservation.email;

      if (isSharedWalkIn && updates.customerName && updates.customerName.trim() !== 'Walk-in') {
        try {
          // Create a new individual customer record
          const { data: newCustomer, error: customerError } = await supabase
            .from('customers')
            .insert({
              first_name: firstName || 'Walk-in',
              last_name: lastName || '',
              phone: updates.customerPhone || null,
              email: updates.customerEmail || null,
              establishment_id: establishmentId,
            })
            .select('id')
            .single();

          if (!customerError && newCustomer) {
            // Re-link the reservation to the new customer
            await supabase
              .from('reservations')
              .update({ customer_id: newCustomer.id })
              .eq('id', supabaseUuid);
            logger.log('✅ Walk-in détaché → nouveau client:', newCustomer.id);
          }
        } catch (detachErr) {
          logger.error('⚠️ Erreur détachement walk-in (non bloquant):', detachErr);
        }
      }

      toast({
        title: "Réservation mise à jour",
        description: "Les modifications ont été enregistrées avec succès.",
      });

      // Rafraîchir les données
      refetch();
    } catch (error) {
      logger.error('Erreur mise à jour réservation:', error);
      if (isPhysicalOverlapError(error)) {
        const { title, description } = getConflictToastMessage();
        toast({ title, description, variant: "destructive" });
      } else {
        toast({
          title: "Erreur",
          description: "Impossible de mettre à jour la réservation.",
          variant: "destructive",
        });
      }
    }
  };

  const handleClientUpdate = async (oldEmail: string, updates: Partial<CustomerData>) => {
    logger.log('🔄 Mise à jour client:', { oldEmail, updates });
    try {
      // Guard: don't update the shared walk-in customer record via this path
      // Walk-in detachment is handled in handleUpdateReservation
      if ((!oldEmail || oldEmail.startsWith('__no_email__')) 
          && updates.firstName === 'Walk-in' && !updates.lastName) {
        logger.log('⏭️ Skip update: shared walk-in customer');
        refetch();
        return;
      }

      const normalizedNewEmail = updates.email?.toLowerCase().trim();
      const normalizedOldEmail = (oldEmail || '').toLowerCase();
      
      // Si l'email a changé, l'edge function a déjà fait la mise à jour
      // (appelé par EditClientDialog avant d'appeler onClientUpdate)
      // On met à jour uniquement les autres champs
      
      // Trouver le customer : priorité à l'ID (fiable, fonctionne pour les clients sans email),
      // fallback par email si l'ID n'est pas disponible
      let customer: { id: string } | null = null;
      let fetchError: any = null;

      if (updates.id) {
        const result = await supabase
          .from('customers')
          .select('id')
          .eq('id', updates.id)
          .eq('establishment_id', establishmentId)
          .maybeSingle();
        customer = result.data;
        fetchError = result.error;
      }

      if (!customer && (normalizedNewEmail || normalizedOldEmail)) {
        const result = await supabase
          .from('customers')
          .select('id')
          .eq('email', normalizedNewEmail || normalizedOldEmail)
          .eq('establishment_id', establishmentId)
          .maybeSingle();
        customer = result.data;
        fetchError = result.error;
      }

      if (fetchError || !customer) {
        throw new Error('Client introuvable');
      }

      // Mettre à jour uniquement les champs non-email (l'email est géré par l'edge function)
      const { error } = await supabase
        .from('customers')
        .update({
          first_name: updates.firstName,
          last_name: updates.lastName,
          phone: updates.phone,
          notes: updates.notes || null,
          marketing_consent: updates.marketingConsent ?? false,
          sms_consent: updates.smsConsent ?? false,
          marketing_consent_at: updates.marketingConsentAt || null,
          sms_consent_at: updates.smsConsentAt || null,
          civility: updates.civility || null,
          country: updates.country || null,
          guest_status: updates.guestStatus || null,
          review_email_consent: updates.reviewEmailConsent ?? false,
          review_email_consent_at: updates.reviewEmailConsentAt || null,
          review_sms_consent: updates.reviewSmsConsent ?? false,
          review_sms_consent_at: updates.reviewSmsConsentAt || null,
          company: updates.company || null,
          professional_email: updates.professionalEmail || null,
          professional_phone: updates.professionalPhone || null,
          birthdate: updates.birthdate || null,
          address: updates.address || null,
          city: updates.city || null,
          zip: updates.zip || null,
          lang: updates.lang || null,
          has_no_show: updates.hasNoShow ?? false,
          is_blacklisted: updates.isBlacklisted ?? false,
          allergies_notes: updates.allergiesNotes || null,
          allergies_tags: updates.allergiesTags || [],
          guest_tags: updates.guestTags || [],
          updated_at: new Date().toISOString(),
        })
        .eq('id', customer.id);

      if (error) throw error;

      toast({
        title: "Client mis à jour",
        description: "Les informations du client ont été enregistrées.",
      });

      // Rafraîchir les données
      refetch();
    } catch (error) {
      logger.error('Erreur mise à jour client:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le client.",
        variant: "destructive",
      });
    }
  };

  // Quick table change handler
  const handleQuickTableChange = async (reservationId: number, tableId: string | null, combinedTableId: string | null) => {
    try {
      const dashboardReservation = reservations.find(r => r.id === reservationId);
      if (!dashboardReservation) throw new Error('Réservation introuvable');
      const supabaseReservation = supabaseReservations.find(r => r.id === dashboardReservation.supabaseId);
      if (!supabaseReservation) {
        throw new Error('Réservation Supabase introuvable');
      }

      const { error } = await supabase
        .from('reservations')
        .update({
          table_id: tableId,
          combined_table_id: combinedTableId,
        })
        .eq('id', supabaseReservation.id);

      if (error) throw error;

      const tableName = tableId 
        ? supabaseTables.find(t => t.id === tableId)?.name 
        : supabaseCombinedTables.find(ct => ct.id === combinedTableId)?.name;

      toast({
        title: "Table modifiée",
        description: `Réservation déplacée vers ${tableName || 'la table sélectionnée'}.`,
      });

      refetch();
    } catch (error) {
      logger.error('Erreur changement de table:', error);
      if (isPhysicalOverlapError(error)) {
        const { title, description } = getConflictToastMessage();
        toast({ title, description, variant: "destructive" });
      } else {
        toast({
          title: "Erreur",
          description: "Impossible de changer la table.",
          variant: "destructive",
        });
      }
    }
  };

  // Centralised swap handler: used by FloorPlan, Timeline and ReservationPreview
  const handleSwapTables = async (aSupabaseId: string, bSupabaseId: string) => {
    const { error } = await supabase.rpc('swap_reservation_tables', {
      _a: aSupabaseId,
      _b: bSupabaseId,
    });
    if (error) {
      if (error.message?.includes('RESERVATION_LOCKED')) {
        toast({
          title: "Échange impossible",
          description: "Une des réservations est verrouillée 🔒.",
          variant: "destructive",
        });
      } else if (isPhysicalOverlapError(error)) {
        const { title, description } = getConflictToastMessage();
        toast({ title, description, variant: "destructive" });
      } else {
        toast({
          title: "Erreur",
          description: error.message ?? "Échange impossible.",
          variant: "destructive",
        });
      }
      throw error;
    }
    toast({ title: "Échange effectué", description: "Les deux réservations ont été échangées." });
    refetch();
  };

  // Waitlist assign handler: assign table + update status
  const handleWaitlistAssign = async (reservationId: number, tableId: string | null, combinedTableId: string | null, newStatus: "en_attente" | "arrivé") => {
    try {
      const dashboardReservation = reservations.find(r => r.id === reservationId);
      if (!dashboardReservation) throw new Error('Réservation introuvable');
      const supabaseReservation = supabaseReservations.find(r => r.id === dashboardReservation.supabaseId);
      if (!supabaseReservation) throw new Error('Réservation Supabase introuvable');

      // Step 1: Assign the table
      const { error: updateError } = await supabase
        .from('reservations')
        .update({
          table_id: tableId,
          combined_table_id: combinedTableId,
        })
        .eq('id', supabaseReservation.id);

      if (updateError) throw updateError;

      // Step 2: Update status only if changing (skip if staying en_attente)
      if (newStatus !== "en_attente") {
        const { data, error: statusError } = await supabase.functions.invoke('update-reservation-status', {
          body: {
            reservationId: supabaseReservation.id,
            status: newStatus,
          }
        });

        if (statusError || data?.error) {
          // Rollback step 1: remove table assignment since status update failed
          await supabase
            .from('reservations')
            .update({ table_id: null, combined_table_id: null })
            .eq('id', supabaseReservation.id);
          throw statusError || new Error(data?.error);
        }
      }

      const tableName = tableId 
        ? supabaseTables.find(t => t.id === tableId)?.name 
        : supabaseCombinedTables.find(ct => ct.id === combinedTableId)?.name;

      toast({
        title: newStatus === "arrivé" ? "Client installé" : "Table assignée",
        description: `${dashboardReservation.name} → ${tableName || 'table sélectionnée'}`,
      });

      setWaitlistAssigning(null);
      refetch();
    } catch (error: any) {
      logger.error('Erreur assignation waitlist:', error);
      if (isPhysicalOverlapError(error)) {
        const { title, description } = getConflictToastMessage();
        toast({ title, description, variant: "destructive" });
      } else {
        toast({
          title: "Erreur",
          description: error?.message || "Impossible d'assigner la table.",
          variant: "destructive",
        });
      }
    }
  };

  const handlePauseWithMessage = async (message: string) => {
    try {
      await updatePauseWithMessage(true, message);
      setShowPauseDialog(false);
      toast({
        title: "Réservations en pause",
        description: "Les clients ne peuvent plus réserver en ligne.",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre les réservations en pause.",
        variant: "destructive",
      });
    }
  };

  const handleSendManualEmail = async () => {
    if (!emailToSend) return;
    
    setSendingEmail(emailToSend.reservationId);
    setEmailDialogOpen(false);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-manual-email', {
        body: {
          reservationId: emailToSend.reservationUuid,
          emailType: emailToSend.emailType
        }
      });

      if (error) throw error;

      let emailTypeText = 'de modification';
      if (emailToSend.emailType === 'cancellation') {
        emailTypeText = "d'annulation";
      } else if (emailToSend.emailType === 'confirmation') {
        emailTypeText = 'de confirmation';
      }

      toast({
        title: "Email envoyé",
        description: `Email ${emailTypeText} envoyé à ${emailToSend.reservationEmail}`,
      });
    } catch (error) {
      logger.error('Erreur envoi email:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer l'email.",
        variant: "destructive",
      });
    } finally {
      setSendingEmail(null);
      setEmailToSend(null);
    }
  };

  const handleSendNewReservationEmail = async () => {
    if (!newReservationForEmail) return;
    
    setShowEmailConfirmDialog(false);
    setSendingEmail(newReservationForEmail.reservationId);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-manual-email', {
        body: {
          reservationId: newReservationForEmail.reservationUuid,
          emailType: 'confirmation'
        }
      });

      if (error) throw error;

      toast({
        title: "Email envoyé",
        description: `Email de confirmation envoyé à ${newReservationForEmail.customerEmail}`,
      });
    } catch (error) {
      logger.error('Erreur envoi email confirmation:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer l'email de confirmation.",
        variant: "destructive",
      });
    } finally {
      setSendingEmail(null);
      setNewReservationForEmail(null);
    }
  };

  const handleSkipNewReservationEmail = () => {
    setShowEmailConfirmDialog(false);
    setNewReservationForEmail(null);
  };

  // Get existing clients from reservations with notes (including customerNotesMap for persistent notes)
  const { clients: existingClients, refetch: refetchClients, customerNotesMap } = useClientsWithNotes({ 
    reservations: convertedReservations 
  });

  // Helper to get persistent customer notes by email
  const getCustomerNotes = (email: string | undefined): string | null => {
    if (!email) return null;
    const customerData = customerNotesMap[email.toLowerCase()];
    return customerData?.notes || null;
  };

  // Helper to get no-show badge for clients with no-show history
  const getNoShowBadge = (email: string | undefined): React.ReactNode => {
    if (!email) return null;
    const customerData = customerNotesMap[email.toLowerCase()];
    if (!customerData?.hasNoShow) return null;
    return (
      <Badge variant="warning" className="text-[10px] px-1.5 py-0">
        No-show
      </Badge>
    );
  };

  // Calculate upcoming reservations (future dates with "planifié" status)
  const upcomingReservations = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return reservations.filter(r => {
      const resDate = new Date(r.reservationDate);
      resDate.setHours(0, 0, 0, 0);
      return resDate >= today && r.status === "planifié";
    });
  }, [reservations]);

   // Calculate reservations for selected date (exclude en_attente from counts)
  const selectedDateReservations = useMemo(() => {
    return reservations.filter(r => 
      format(r.reservationDate, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd") &&
      r.status !== "annulé" &&
      r.status !== "en_attente"
    );
  }, [reservations, selectedDate]);

  // Waitlist reservations for selected date
  const waitlistReservations = useMemo(() => {
    return reservations
      .filter(r =>
        format(r.reservationDate, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd") &&
        r.status === "en_attente"
      )
      .map(r => ({
        ...r,
        tableName: r.tableId
          ? supabaseTables.find(t => t.id === r.tableId)?.name
          : r.combinedTableId
            ? supabaseCombinedTables.find(ct => ct.id === r.combinedTableId)?.name
            : undefined,
      }));
  }, [reservations, selectedDate, supabaseTables, supabaseCombinedTables]);

  const selectedDatePersons = useMemo(() => {
    return selectedDateReservations.reduce((sum, r) => sum + r.persons + (r.numberOfChildren || 0), 0);
  }, [selectedDateReservations]);

  // Calculate new reservations since last seen (DB-backed)
  const newReservations = useMemo(() => {
    if (!dashboardLastSeenAt) return [];
    return reservations
      .filter(r => r.createdAt > dashboardLastSeenAt)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [reservations, dashboardLastSeenAt]);

  // Clean up legacy localStorage on mount
  useEffect(() => {
    localStorage.removeItem('readReservationIds');
  }, []);

  // Use effective time slots for the selected date (regular slots or date-specific overrides),
  // plus les plages des salles à horaires propres pour que leurs réservations restent visibles.
  const timeSlots = useMemo(() => {
    return getStaffSlotsForDate(selectedDate, supabaseTimeSlots, supabaseDateOverrides, zoneWindows, dashboardActiveZoneIds)
      .map(ts => ({
        start: ts.start.substring(0, 5), // Format "HH:mm" pour matcher avec les réservations
        end: ts.end.substring(0, 5)
      }));
  }, [supabaseTimeSlots, supabaseDateOverrides, selectedDate, zoneWindows, dashboardActiveZoneIds]);

  // Totaux jour + par service (services du restaurant uniquement, sans plages de salles)
  const serviceCounts = useMemo(() => {
    const services = getTimeSlotsForDate(selectedDate, supabaseTimeSlots, supabaseDateOverrides)
      .map(ts => ({ start: ts.start.substring(0, 5), end: ts.end.substring(0, 5) }));
    return computeServiceCounts(selectedDateReservations, services);
  }, [selectedDateReservations, supabaseTimeSlots, supabaseDateOverrides, selectedDate]);

  // Créneaux complets (avec durée) pour le panneau « Heures en ligne »
  const getReservationStatus = (reservation: typeof reservations[0]) => {
    // Check status first
    if (reservation.status === "arrivé") {
      return { type: "arrived", label: "Arrivé", delay: 0, variant: "success" as const };
    }
    if (reservation.status === "annulé") {
      if (reservation.cancellationReason === 'no-show') {
        return { type: "no-show", label: "No-show", delay: 0, variant: "secondary" as const };
      }
      return { type: "canceled", label: "Annulé", delay: 0, variant: "secondary" as const };
    }

    // Only check delay if reservation is today
    if (!isToday(reservation.reservationDate)) {
      return { type: "scheduled", label: "Planifié", delay: 0, variant: "default" as const };
    }

    const [hours, minutes] = reservation.time.split(":").map(Number);
    const reservationTime = new Date(currentTime);
    reservationTime.setHours(hours, minutes, 0, 0);

    const delayMinutes = differenceInMinutes(currentTime, reservationTime);

    if (delayMinutes < 0) {
      return { type: "on-time", label: "À l'heure", delay: 0, variant: "default" as const };
    } else if (delayMinutes >= 0 && delayMinutes < 15) {
      return { type: "on-time", label: "À l'heure", delay: delayMinutes, variant: "default" as const };
    } else if (delayMinutes >= 15 && delayMinutes < 30) {
      return { type: "late", label: "En retard", delay: delayMinutes, variant: "warning" as const };
    } else if (delayMinutes >= 30 && delayMinutes < 45) {
      return { type: "very-late", label: "Très en retard", delay: delayMinutes, variant: "warning" as const };
    } else {
      return { type: "no-show-probable", label: "No-show probable", delay: delayMinutes, variant: "destructive" as const };
    }
  };

  const getFormattedDate = (date: Date) => {
    if (isToday(date)) return "Aujourd'hui";
    if (isTomorrow(date)) return "Demain";
    if (isYesterday(date)) return "Hier";
    return format(date, "EEEE d MMMM yyyy", { locale: fr });
  };

  const groupedReservations = useMemo(() => {
    const isTimeMode = reservationSettings?.bookingMode === 'time';
    const reservationDuration = reservationSettings?.reservationDurationMinutes ?? 120;
    
    // Get all reservations for the selected date
    const dateReservationsFiltered = reservations.filter(
      r => format(r.reservationDate, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd") &&
        r.status !== "en_attente" &&
        (!hideCanceled || r.status !== "annulé")
    );
    
    if (isTimeMode) {
      // In TIME mode, group by the actual reservation times
      const uniqueTimes = [...new Set(dateReservationsFiltered.map(r => r.time))].sort();
      
      // If no reservations, still show the time slots for adding new ones
      if (uniqueTimes.length === 0) {
        return timeSlots.map(slot => ({
          slot,
          reservations: [],
          totalPersons: 0,
          lateCount: 0,
          tablesStatus: supabaseTables.filter(table => {
            const weekdayAvailability = supabaseWeekdayAvailabilities.find(w => w.tableId === table.id);
            return isTableAvailableOnDate(table, selectedDate, supabaseAvailabilities, weekdayAvailability || null);
          }).map(table => ({ table, isBooked: false, reservation: undefined })),
          combinedTablesStatus: supabaseCombinedTables.filter(ct => ct.isActive).map(ct => ({
            combinedTable: ct,
            isBooked: false,
            reservation: undefined
          })),
          isTimeMode: true,
        }));
      }
      
      return uniqueTimes.map(time => {
        const timeReservations = dateReservationsFiltered.filter(r => r.time === time)
          .sort((a, b) => {
            const statusOrder = { "planifié": 0, "arrivé": 1, "annulé": 2 };
            return statusOrder[a.status] - statusOrder[b.status];
          });
        
        const totalPersons = timeReservations
          .filter(r => r.status === "planifié" || r.status === "arrivé")
          .reduce((sum, r) => sum + r.persons + (r.numberOfChildren || 0), 0);
        
        const lateCount = timeReservations.filter(r => {
          const status = getReservationStatus(r);
          return status.type === "late" || status.type === "very-late" || status.type === "no-show-probable";
        }).length;

        const availableTables = supabaseTables.filter(table => {
          const weekdayAvailability = supabaseWeekdayAvailabilities.find(w => w.tableId === table.id);
          return isTableAvailableOnDate(table, selectedDate, supabaseAvailabilities, weekdayAvailability || null);
        });

        // Use exact start-time matching for display consistency with the reservation list
        const blockedIds = new Set<string>();
        timeReservations
          .filter(r => r.status !== "annulé")
          .forEach(r => {
            if (r.tableId) blockedIds.add(r.tableId);
            if (r.combinedTableId) {
              blockedIds.add(r.combinedTableId);
              const ct = supabaseCombinedTables.find(c => c.id === r.combinedTableId);
              if (ct) ct.tableIds.forEach(tid => blockedIds.add(tid));
            }
          });

        const tablesStatus = availableTables.map(table => ({
          table,
          isBooked: blockedIds.has(table.id),
          reservation: timeReservations.find(r => r.tableId === table.id && r.status !== "annulé")
        }));

        const activeCombinedTables = supabaseCombinedTables.filter(ct => ct.isActive);
        const combinedTablesStatus = activeCombinedTables.map(ct => ({
          combinedTable: ct,
          isBooked: blockedIds.has(ct.id) || ct.tableIds.some(tid => blockedIds.has(tid)),
          reservation: timeReservations.find(r => r.combinedTableId === ct.id && r.status !== "annulé")
        }));

        // Calculate end time based on reservation duration
        const [hours, minutes] = time.split(':').map(Number);
        const endMinutes = hours * 60 + minutes + reservationDuration;
        const endHours = Math.floor(endMinutes / 60);
        const endMins = endMinutes % 60;
        const endTime = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;

        return {
          slot: { start: time, end: endTime },
          reservations: timeReservations,
          totalPersons,
          lateCount,
          tablesStatus,
          combinedTablesStatus,
          isTimeMode: true,
        };
      });
    }
    
    // SLOT mode: original behavior
    return timeSlots.map(slot => {
      // Filter reservations for this slot and date
      const slotReservations = dateReservationsFiltered.filter(
        r => r.time === slot.start
      ).sort((a, b) => {
        // Sort by status: planifié first, then arrivé, then annulé
        const statusOrder = { "planifié": 0, "arrivé": 1, "annulé": 2 };
        return statusOrder[a.status] - statusOrder[b.status];
      });
      
      const totalPersons = slotReservations
        .filter(r => r.status === "planifié" || r.status === "arrivé")
        .reduce((sum, r) => sum + r.persons + (r.numberOfChildren || 0), 0);
      const lateCount = slotReservations.filter(r => {
        const status = getReservationStatus(r);
        return status.type === "late" || status.type === "very-late" || status.type === "no-show-probable";
      }).length;

      // Get available tables for this slot
      const availableTables = supabaseTables.filter(table => {
        const weekdayAvailability = supabaseWeekdayAvailabilities.find(w => w.tableId === table.id);
        return isTableAvailableOnDate(table, selectedDate, supabaseAvailabilities, weekdayAvailability || null);
      });

      // Build conflict options for TIME mode support
      const conflictOptions = reservationSettings?.bookingMode === 'time'
        ? { bookingMode: 'time' as const, reservationDurationMinutes: reservationSettings.reservationDurationMinutes ?? 120 }
        : undefined;

      // Use centralized function to get blocked table IDs (automatically filters cancellations)
      // Using fresh Supabase data directly instead of local state for immediate status updates
      const blockedIds = getBlockedTableIds(
        supabaseReservations,
        supabaseCombinedTables,
        selectedDate,
        slot.start,
        conflictOptions
      );

      // Determine which tables are booked vs available
      const tablesStatus = availableTables.map(table => ({
        table,
        isBooked: blockedIds.has(table.id),
        reservation: slotReservations.find(
          r => r.tableId === table.id && r.status !== "annulé"
        )
      }));

      // Get active combined tables and their status
      const activeCombinedTables = supabaseCombinedTables.filter(ct => ct.isActive);
      const combinedTablesStatus = activeCombinedTables.map(ct => ({
        combinedTable: ct,
        // Vérifie si la combinaison OU une de ses tables individuelles est bloquée
        isBooked: blockedIds.has(ct.id) || ct.tableIds.some(tid => blockedIds.has(tid)),
        reservation: slotReservations.find(
          r => r.combinedTableId === ct.id && r.status !== "annulé"
        )
      }));

      return { slot, reservations: slotReservations, totalPersons, lateCount, tablesStatus, combinedTablesStatus, isTimeMode: false };
    });
  }, [reservations, selectedDate, supabaseReservations, supabaseCombinedTables, supabaseTables, supabaseAvailabilities, supabaseWeekdayAvailabilities, reservationSettings, timeSlots, hideCanceled]);

  const getStatusBadge = (status: string, cancellationReason?: string) => {
    switch (status) {
      case "planifié":
        return <Badge className="bg-accent text-white">Planifié</Badge>;
      case "arrivé":
        return <Badge className="bg-green-500 text-white">Arrivé</Badge>;
      case "annulé":
        if (cancellationReason === 'no-show') {
          return <Badge className="bg-gray-700 text-white">No-show</Badge>;
        }
        return <Badge variant="outline">Annulé</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getLoyaltyBadge = (email: string | undefined) => {
    const level = getClientLoyaltyLevel(email, existingClients);
    
    switch (level) {
      case 'vip':
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white text-[10px] px-1.5 py-0 h-4 ml-1.5 gap-0.5 cursor-help">
                  <Crown className="w-2.5 h-2.5" />
                  <span className="hidden sm:inline">VIP</span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>Client VIP (10+ visites)</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case 'loyal':
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge className="bg-purple-500 text-white text-[10px] px-1.5 py-0 h-4 ml-1.5 gap-0.5 cursor-help">
                  <Sparkles className="w-2.5 h-2.5" />
                  <span className="hidden sm:inline">Fidèle</span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>Client fidèle (5+ visites)</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case 'returning':
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 ml-1.5 cursor-help">
                  <span className="hidden sm:inline">Habitué</span>
                  <span className="sm:hidden">2+</span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>Client habitué (2+ visites)</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      default:
        return null;
    }
  };

  // Le badge du plan est maintenant géré par le composant PlanBadge centralisé

  // Alerte d'expiration du plan trial
  const getTrialAlert = () => {
    if (!isAdmin || !establishmentSettings || establishmentSettings.planType !== 'trial') return null;
    
    const trialEndsAt = establishmentSettings.trialEndsAt;
    if (!trialEndsAt) return null;
    
    const daysLeft = Math.ceil((new Date(trialEndsAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 7 && daysLeft > 0) {
      return {
        type: 'warning' as const,
        title: `Votre période d'essai se termine dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}`,
        description: 'Contactez-nous pour continuer à utiliser Piktable.'
      };
    }
    
    if (daysLeft <= 0) {
      return {
        type: 'error' as const,
        title: 'Votre période d\'essai est terminée',
        description: 'Certaines fonctionnalités peuvent être limitées. Contactez-nous pour activer votre abonnement.'
      };
    }
    
    return null;
  };

  const trialAlert = getTrialAlert();

  if (loading || settingsLoading || !reservationSettings) {
    return <PiktableLoader message="Chargement des données..." />;
  }


  // Simplified summary panel rendered inside the right-side Sheet,
  // triggered by the Bell button in the header.
  const dashboardSummaryBlock = (
    <div className="space-y-6">
      <div>
        <p className="text-4xl font-serif font-bold text-foreground tabular-nums">
          {upcomingReservations.length}
        </p>
        <p className="text-sm text-muted-foreground mt-1">Réservations à venir</p>
      </div>

      <div className="border-t pt-6">
        <p className="text-4xl font-serif font-bold text-foreground tabular-nums">
          {upcomingReservations.reduce((sum, r) => sum + r.persons, 0)}
        </p>
        <p className="text-sm text-muted-foreground mt-1">Personnes attendues</p>
      </div>

      <div className="border-t pt-6">
        <div className="flex items-baseline gap-2">
          <p className="text-4xl font-serif font-bold text-foreground tabular-nums">
            {newReservations.length}
          </p>
          {newReservations.length > 0 && (
            <span
              aria-hidden
              className="h-2 w-2 rounded-full bg-primary"
            />
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Nouvelles depuis votre dernière connexion
        </p>

        {newReservations.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-3 -ml-2 text-muted-foreground hover:text-foreground"
            onClick={() => {
              updateDashboardLastSeen();
              setSummaryCollapsed(true);
            }}
          >
            <CheckCheck className="w-4 h-4 mr-2" />
            Tout marquer comme lu
          </Button>
        )}
      </div>
    </div>
  );

  const dashboardMenuSections: DashboardMenuSection[] = [
    {
      id: "today",
      label: "Actions du jour",
      actions: [
        { id: "availability", label: "Disponibilités", icon: Clock, onSelect: () => setAvailabilityOpen(true), phoneOnly: true },
        { id: "walk-in", label: "Walk-in", icon: UserPlus, onSelect: () => setWalkInOpen(true), phoneOnly: true },
        { id: "past-reservation", label: "Réservation passée", icon: CalendarIcon, onSelect: () => setRetroactiveDialogOpen(true) },
        {
          id: "closed-slot-reservation",
          label: "Nouvelle résa (créneau fermé)",
          icon: AlertTriangle,
          iconClassName: "text-destructive",
          onSelect: () => setForceOverrideDialogOpen(true),
        },
        {
          id: "pause",
          label: reservationSettings?.isPaused ? "Réactiver les réservations" : "Mettre en pause",
          icon: reservationSettings?.isPaused ? Play : Pause,
          onSelect: () => {
            if (reservationSettings?.isPaused) {
              updatePauseWithMessage(false, reservationSettings.pauseMessage);
              toast({
                title: "Réservations activées",
                description: "Les réservations en ligne sont à nouveau disponibles.",
              });
            } else {
              setShowPauseDialog(true);
            }
          },
        },
      ],
    },
    {
      id: "management",
      label: "Gestion",
      actions: [
        { id: "tables", label: "Tables", icon: UtensilsCrossed, onSelect: () => navigate('/dashboard/tables') },
        { id: "slots", label: "Gestion des créneaux", icon: Clock, onSelect: () => navigate('/dashboard/time-slots') },
        { id: "notifications", label: "Notifications", icon: Bell, onSelect: () => navigate('/dashboard/notifications'), visible: isAdmin },
        { id: "email-settings", label: "Paramètres d'email", icon: Settings, onSelect: () => navigate('/dashboard/email-settings'), visible: isAdmin },
        { id: "sent-emails", label: "Emails envoyés", icon: Mail, onSelect: () => navigate('/dashboard/emails'), visible: isAdmin },
        { id: "print", label: "Imprimer les réservations", icon: Printer, onSelect: () => setShowPrintDialog(true) },
        { id: "statistics", label: "Statistiques", icon: BarChart3, onSelect: () => navigate('/dashboard/statistics'), visible: isAdmin },
      ],
    },
    {
      id: "administration",
      label: "Administration",
      actions: [
        { id: "establishment", label: "Établissement", icon: Building, onSelect: () => navigate('/dashboard/establishment'), visible: isAdmin },
        { id: "billing", label: "Facturation", icon: Receipt, onSelect: () => navigate('/dashboard/billing'), visible: isAdmin },
        { id: "users", label: "Utilisateurs", icon: Users, onSelect: () => navigate('/dashboard/users'), visible: isAdmin },
        { id: "global-admin", label: "Administration globale", icon: Building, onSelect: () => navigate('/admin'), visible: isSuperAdmin },
        {
          id: "install",
          label: "Installer l'app",
          icon: Smartphone,
          visible: !isInstalled,
          onSelect: async () => {
            if (canPromptInstall) {
              await promptInstall();
            } else {
              setShowPWADialog(true);
            }
          },
        },
      ],
    },
    {
      id: "help",
      label: "Aide",
      actions: [
        { id: "ai-chat", label: "Chat IA", icon: Sparkles, onSelect: () => setAiChatOpen(true) },
        { id: "feedback", label: "Feedback", icon: MessageSquarePlus, onSelect: () => setFeedbackOpen(true) },
        { id: "guide", label: "Guide d'utilisation", icon: BookOpen, onSelect: () => navigate('/dashboard/help') },
      ],
    },
    {
      id: "account",
      label: "Compte",
      actions: [
        { id: "password", label: "Modifier mon mot de passe", icon: KeyRound, onSelect: () => setChangePasswordOpen(true) },
        {
          id: "sign-out",
          label: "Déconnexion",
          icon: LogOut,
          onSelect: async () => {
            await signOut();
            navigate('/login');
          },
        },
      ],
    },
  ];

  const visibleMenuSections = dashboardMenuSections
    .map((section) => ({
      ...section,
      actions: section.actions.filter((action) => action.visible !== false),
    }))
    .filter((section) => section.actions.length > 0);

  const mobilePinnedActionIds = new Set(["feedback", "password", "sign-out"]);
  const mobileScrollableSections = visibleMenuSections
    .map((section) => ({
      ...section,
      actions: section.actions.filter(
        (action) => !mobilePinnedActionIds.has(action.id) && (!action.phoneOnly || isMobile),
      ),
    }))
    .filter((section) => section.actions.length > 0);
  const mobilePinnedActions = visibleMenuSections
    .flatMap((section) => section.actions)
    .filter((action) => mobilePinnedActionIds.has(action.id));

  const runMobileMenuAction = (action: DashboardMenuAction) => {
    setMobileMenuOpen(false);
    void action.onSelect();
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {!isSuperAdmin && <ManagerNotificationDialog establishmentId={establishmentId} />}
      {/* Super Admin Override Banner */}
      {isSuperAdmin && overrideEstablishmentId && (
        <div className="bg-blue-50 dark:bg-blue-950/30 border-b border-blue-200 dark:border-blue-800 px-4 py-2">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
              <Eye className="w-4 h-4" />
              <span className="text-sm">
                Mode Super Admin : <strong>{establishmentName}</strong>
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setOverrideEstablishmentId(null)}
              className="text-blue-800 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/50"
            >
              <X className="w-4 h-4 mr-1" />
              Quitter
            </Button>
          </div>
        </div>
      )}

      <div className="flex">
        {!isMobile && (
          <DashboardNavRail
            activeTab={activeTab}
            onChange={(v: DashboardTabValue) => handleTabChange(v)}
            terraceEnabled={!!reservationSettings?.terraceEnabled}
            collapsed={navRailCollapsed}
            onToggleCollapsed={toggleNavRail}
          />
        )}
        <div className="flex-1 min-w-0 overflow-x-hidden">

      {/* Header */}
      <header className="bg-card border-b">
        <div className="container mx-auto px-3 md:px-4 py-2 md:py-4 overflow-hidden">
          <div className="flex items-center justify-between gap-1 md:gap-2">
            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
              {/* Super Admin Selector */}
              {isSuperAdmin && (
                <EstablishmentSelector />
              )}
              
              {/* Title - only show when not super admin or no override */}
              {(!isSuperAdmin || !overrideEstablishmentId) && (
                <h1 className="text-base md:text-2xl font-serif font-bold truncate">
                  Dashboard
                </h1>
              )}
            </div>
            <div className="flex items-center gap-0.5 md:gap-2 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={() => setAvailabilityOpen(true)} className="hidden md:inline-flex">
                <Clock className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Disponibilités</span>
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="sm" onClick={() => setWalkInOpen(true)} className="hidden md:inline-flex">
                      <UserPlus className="w-4 h-4 md:mr-2" />
                      <span className="hidden md:inline">Walk-in</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Créer un walk-in pour aujourd'hui</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleSummary}
                      aria-label="Ouvrir le tableau de bord"
                      className="relative"
                    >
                      <Bell className="w-5 h-5" />
                      {newReservations.length > 0 && (
                        <span
                          aria-hidden
                          className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-primary ring-2 ring-background"
                        />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Tableau de bord</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <WhatsNewPopover />
              <Button
                variant="ghost"
                size="icon"
                onClick={handleManualRefresh}
                disabled={isRefreshing}
                title="Rafraîchir les données"
              >
                <RefreshCw className={cn("w-5 h-5", isRefreshing && "animate-spin")} />
              </Button>

              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Menu"
                  aria-label="Ouvrir le menu"
                  className="lg:hidden"
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <MoreVertical className="w-5 h-5" />
                </Button>
                <SheetContent
                  side="right"
                  className="flex h-[100dvh] w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-sm"
                >
                  <SheetHeader className="shrink-0 border-b px-5 py-4 pr-12 text-left">
                    <SheetTitle>Menu</SheetTitle>
                    <SheetDescription>Navigation et réglages du tableau de bord</SheetDescription>
                  </SheetHeader>

                  <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3" aria-label="Menu du tableau de bord">
                    {mobileScrollableSections.map((section) => (
                      <div key={section.id} className="mb-4 last:mb-0">
                        <p className="px-2 pb-1.5 text-xs font-semibold text-muted-foreground">
                          {section.label}
                        </p>
                        <div className="space-y-1">
                          {section.actions.map((action) => {
                            const Icon = action.icon;
                            return (
                              <Button
                                key={action.id}
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start px-3"
                                onClick={() => runMobileMenuAction(action)}
                              >
                                <Icon className={cn("h-5 w-5", action.iconClassName)} />
                                <span className="truncate">{action.label}</span>
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </nav>

                  <div className="shrink-0 border-t bg-background px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3">
                    <div className="space-y-1">
                      {mobilePinnedActions.map((action) => {
                        const Icon = action.icon;
                        return (
                          <Button
                            key={action.id}
                            type="button"
                            variant="ghost"
                            className={cn(
                              "h-11 w-full justify-start px-3",
                              action.id === "sign-out" && "text-destructive hover:text-destructive",
                            )}
                            onClick={() => runMobileMenuAction(action)}
                          >
                            <Icon className="h-5 w-5" />
                            <span className="truncate">{action.label}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" title="Menu" aria-label="Ouvrir le menu" className="hidden lg:inline-flex">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {visibleMenuSections.map((section, sectionIndex) => (
                    <div key={section.id}>
                      {sectionIndex > 0 && <DropdownMenuSeparator />}
                      {section.actions.map((action) => {
                        const Icon = action.icon;
                        return (
                          <DropdownMenuItem
                            key={action.id}
                            onClick={() => void action.onSelect()}
                            className={action.phoneOnly ? "md:hidden" : undefined}
                          >
                            <Icon className={cn("w-4 h-4 mr-2", action.iconClassName)} />
                            {action.label}
                          </DropdownMenuItem>
                        );
                      })}
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 md:px-4 lg:px-6 py-3 md:py-4 lg:py-5 max-w-7xl pb-24 md:pb-4 lg:pb-5 min-w-0 overflow-x-hidden">
        {/* Trial Alert */}
        {trialAlert && (
          <div className={cn(
            "mb-4 p-4 rounded-lg flex items-center gap-3",
            trialAlert.type === 'error' 
              ? "bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800" 
              : "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
          )}>
            <AlertTriangle className={cn("w-5 h-5 flex-shrink-0", trialAlert.type === 'error' ? "text-red-600" : "text-amber-600")} />
            <div>
              <p className={cn("font-medium", trialAlert.type === 'error' ? "text-red-800 dark:text-red-200" : "text-amber-800 dark:text-amber-200")}>
                {trialAlert.title}
              </p>
              <p className={cn("text-sm", trialAlert.type === 'error' ? "text-red-700 dark:text-red-300" : "text-amber-700 dark:text-amber-300")}>
                {trialAlert.description}
              </p>
            </div>
          </div>
        )}

        {/* Spam Alert Banner */}
        <SpamAlertBanner establishmentId={establishmentId} />

        {/* Trial Help Banner */}
        <TrialHelpBanner 
          planType={establishmentSettings?.planType} 
          establishmentId={establishmentId}
          hasReservations={reservations.length > 0}
        />


        

        {/* General NewReservationDialog removed - each view (timeline, list, grouped) has its own with correct date/timeSlot */}

        {/* Edit Reservation Dialog removed - each view has its own instance */}

        {/* Onboarding Widget - shows when configuration is incomplete (admin only - CTAs link to admin pages) */}
        {showOnboardingWidget && isAdmin && (
          <div className="mb-6">
            <OnboardingWidget status={configStatus} onDismiss={dismissWidget} />
          </div>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-2 md:space-y-3">


          <TabsContent value="reservations" className="space-y-4 md:space-y-6">
            {/* Date Selector - Stacked on mobile */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4 md:mb-5">
              {/* Row 1: Date navigation - centered on mobile */}
              <div className="flex items-center justify-center gap-2 md:gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 md:h-10 md:w-10"
                  onClick={() => setSelectedDate(subDays(selectedDate, 1))}
                >
                  <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "min-w-[160px] md:min-w-[250px] justify-center font-normal px-2 md:px-4",
                      )}
                    >
                      <CalendarIcon className="mr-1 md:mr-2 h-4 w-4" />
                      <div className="text-center">
                        <div className="font-serif font-bold capitalize text-xs md:text-sm">
                          {getFormattedDate(selectedDate)}
                        </div>
                        <div className="text-[10px] md:text-xs text-muted-foreground">
                          {format(selectedDate, "d MMMM yyyy", { locale: fr })}
                        </div>
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-50 bg-background" align="center">
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => date && setSelectedDate(date)}
                      initialFocus
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 md:h-10 md:w-10"
                  onClick={() => setSelectedDate(addDays(selectedDate, 1))}
                >
                  <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                </Button>
              </div>
              
              {/* Row 2: Secondary actions - space-between on mobile, right-aligned on desktop */}
              <div className="flex items-center justify-between md:justify-end gap-2 md:gap-4">
                {!isToday(selectedDate) && (
                  <Button 
                    onClick={() => setSelectedDate(new Date())}
                    size="sm"
                    className="text-xs md:text-sm"
                  >
                    <span className="md:hidden">Ajd</span>
                    <span className="hidden md:inline">Aujourd'hui</span>
                  </Button>
                )}
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {format(currentTime, "HH:mm", { locale: fr })}
                  </span>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setHideTableStatus(!hideTableStatus)}
                      >
                        {hideTableStatus ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {hideTableStatus ? "Afficher l'état des tables" : "Masquer l'état des tables"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setHideCanceled(!hideCanceled)}
                        aria-label={hideCanceled ? "Afficher les réservations annulées" : "Masquer les réservations annulées"}
                      >
                        <CalendarX className={cn("w-4 h-4", hideCanceled && "text-muted-foreground/50")} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {hideCanceled ? "Afficher les annulées" : "Masquer les annulées"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          const next = viewMode === 'grouped' ? 'list' : viewMode === 'list' ? 'timeline' : 'grouped';
                          setViewMode(next);
                        }}
                      >
                        {viewMode === 'grouped' ? (
                          <List className="w-4 h-4" />
                        ) : viewMode === 'list' ? (
                          <GanttChart className="w-4 h-4" />
                        ) : (
                          <LayoutGrid className="w-4 h-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {viewMode === 'grouped' ? "Vue liste chronologique" : viewMode === 'list' ? "Vue timeline" : "Vue groupée par créneau"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setOnlineClosureOpen(true)}
                        aria-label="Fermer des heures à la réservation en ligne"
                      >
                        <Ban className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Fermer des heures à la réservation en ligne
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <OnlineClosureDialog
              establishmentId={establishmentId}
              open={onlineClosureOpen}
              onOpenChange={setOnlineClosureOpen}
              initialDate={selectedDate}
            />

        {/* Résumé jour + service */}
        <DayServiceSummary dayLabel={getFormattedDate(selectedDate)} counts={serviceCounts} />


        {/* Terrasse désactivée : réservations à venir encore sur la terrasse */}
        {establishmentId && reservationSettings && (
          <TerraceReplaceBanner
            establishmentId={establishmentId}
            terraceEnabled={!!reservationSettings.terraceEnabled}
            reservations={supabaseReservations}
            tables={supabaseTables}
            combinedTables={supabaseCombinedTables}
            availabilities={supabaseAvailabilities}
            weekdayAvailabilities={supabaseWeekdayAvailabilities}
            bookablePeriods={supabaseBookablePeriods}
            childrenCapacityMode={(reservationSettings.childrenCapacityMode as 'full' | 'half' | 'none') ?? 'full'}
            durationMinutes={reservationSettings.reservationDurationMinutes ?? 120}
            customAssignmentOrderEnabled={reservationSettings.customAssignmentOrderEnabled ?? false}
          />
        )}

        {/* Waitlist Banner */}
        <WaitlistBanner
          waitlistReservations={waitlistReservations}
          onAssignTable={(reservationId) => {
            const r = reservations.find(res => res.id === reservationId);
            if (r) setWaitlistAssigning(r);
          }}
          onCancel={(reservationId) => {
            handleUpdateReservationStatus(reservationId, "annulé");
          }}
          onEdit={(reservationId) => {
            const r = reservations.find(res => res.id === reservationId);
            if (r) setEditingReservation(r);
          }}
          onMarkArrived={(reservationId) => {
            handleUpdateReservationStatus(reservationId, "arrivé");
          }}
        />

        {/* Reservations by Time Slot, List View, or Timeline View */}
        {viewMode === 'timeline' ? (
          <div className="space-y-3 md:space-y-4">

            <DashboardTimelineView
              reservations={hideCanceled ? reservations.filter(r => r.status !== "annulé") : reservations}
              selectedDate={selectedDate}
              tables={supabaseTables}
              combinedTables={supabaseCombinedTables}
              timeSlots={timeSlots}
              reservationDurationMinutes={reservationSettings?.reservationDurationMinutes ?? 120}
              bookingMode={(reservationSettings?.bookingMode as 'slot' | 'time') ?? 'slot'}
              onEdit={setEditingReservation}
              onUpdateStatus={handleUpdateReservationStatus}
              onQuickTableChange={handleQuickTableChange}
              onSwapTables={async (aSup, bSup) => { await handleSwapTables(aSup, bSup); }}
              onSendEmail={(data) => {
                setEmailToSend(data);
                setEmailDialogOpen(true);
              }}
              sendingEmail={sendingEmail}
              getReservationStatus={getReservationStatus}
              getLoyaltyBadge={getLoyaltyBadge}
              getNoShowBadge={getNoShowBadge}
              getCustomerNotes={getCustomerNotes}
              convertedReservations={convertedReservations}
              availabilities={supabaseAvailabilities}
              weekdayAvailabilities={supabaseWeekdayAvailabilities}
              childrenEnabled={reservationSettings?.childrenEnabled ?? false}
              childrenCapacityMode={(reservationSettings?.childrenCapacityMode as 'full' | 'half' | 'none') ?? 'full'}
              establishmentId={establishmentId || undefined}
            />
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-3 md:space-y-4">

            <DashboardListView
              reservations={hideCanceled ? reservations.filter(r => r.status !== "annulé") : reservations}
              selectedDate={selectedDate}
              tables={supabaseTables}
              combinedTables={supabaseCombinedTables}
              convertedReservations={convertedReservations}
              availabilities={supabaseAvailabilities}
              weekdayAvailabilities={supabaseWeekdayAvailabilities}
              closures={closures}
              isMobile={isMobile}
              getReservationStatus={getReservationStatus}
              getStatusBadge={getStatusBadge}
              getLoyaltyBadge={getLoyaltyBadge}
              getNoShowBadge={getNoShowBadge}
              getCustomerNotes={getCustomerNotes}
              onUpdateStatus={handleUpdateReservationStatus}
              onEdit={setEditingReservation}
              onPreview={setPreviewReservation}
              onQuickTableChange={handleQuickTableChange}
              onSwapTables={async (a, b) => { await handleSwapTables(a, b); }}

              onSendEmail={(data) => {
                setEmailToSend(data);
                setEmailDialogOpen(true);
              }}
              sendingEmail={sendingEmail}
              reservationDurationMinutes={reservationSettings?.reservationDurationMinutes ?? 120}
              reservationSettings={reservationSettings ? {
                bookingMode: reservationSettings.bookingMode as 'slot' | 'time',
                reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
              } : undefined}
            />
          </div>
        ) : (
        <div className="space-y-3 md:space-y-4">
          {groupedReservations.map(({ slot, reservations, totalPersons, lateCount, tablesStatus, combinedTablesStatus }) => {
            const slotPassed = isSlotPassed(slot.end);
            const isCollapsed = collapsedSlots.has(slot.start) || (slotPassed && !collapsedSlots.has(`expanded_${slot.start}`));
            
            return (
              <Card key={slot.start} className="p-3 md:p-4 lg:p-5">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <div className="flex items-center gap-2 md:gap-3 flex-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleSlotCollapse(slot.start, slot.end)}
                      className="h-7 w-7 md:h-8 md:w-8"
                    >
                      {isCollapsed ? (
                        <ChevronDown className="w-4 h-4 md:w-5 md:h-5" />
                      ) : (
                        <ChevronUp className="w-4 h-4 md:w-5 md:h-5" />
                      )}
                    </Button>
                    <div>
                      <h3 className="text-base md:text-lg font-serif font-semibold">
                        {reservationSettings?.bookingMode === 'time' 
                          ? `${slot.start} → ${slot.end}`
                          : `Créneau ${slot.start} - ${slot.end}`
                        }
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {(() => {
                          const n = reservations.filter(r => r.status === "planifié" || r.status === "arrivé").length;
                          return `${n} résa${n > 1 ? "s" : ""} · `;
                        })()}
                        {totalPersons} personne{totalPersons > 1 ? "s" : ""} attendue{totalPersons > 1 ? "s" : ""}
                      </p>
                    </div>
                    {lateCount > 0 && (
                      <Badge variant="destructive" className="ml-1 md:ml-2 text-xs">
                        {lateCount} en retard
                      </Badge>
                    )}
                  </div>


                </div>

                {!isCollapsed && (
                  <>

                    {/* Visual table status - conditionally rendered */}
                    {!hideTableStatus && (isMobile ? (
                      <Collapsible 
                        open={!collapsedTableStatus.has(slot.start)}
                        onOpenChange={() => toggleTableStatusCollapse(slot.start)}
                        className="mb-6"
                      >
                        <CollapsibleTrigger className="w-full active:scale-95 transition-transform">
                          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                            <h4 className="text-sm font-semibold">État des tables</h4>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {tablesStatus.filter(t => t.isBooked).length + combinedTablesStatus.filter(ct => ct.isBooked).length}/
                                {tablesStatus.length + combinedTablesStatus.length} occupées
                              </span>
                              {collapsedTableStatus.has(slot.start) ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronUp className="w-4 h-4" />
                              )}
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent className="pt-4">
                          {/* Tables simples */}
                          {tablesStatus.length > 0 && (
                            <div className="mb-4">
                              <p className="text-xs text-muted-foreground mb-2">Tables individuelles</p>
                              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
                                {tablesStatus.map(({ table, isBooked, reservation }) => (
                                  <div
                                    key={table.id}
                                    className={`p-3 rounded-lg border-2 transition-all ${
                                      isBooked
                                        ? "bg-red-50 border-red-300 dark:bg-red-950/30 dark:border-red-800"
                                        : "bg-green-50 border-green-300 dark:bg-green-950/30 dark:border-green-800"
                                    }`}
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-semibold text-sm">{table.name}</span>
                                      <Badge
                                        variant={isBooked ? "destructive" : "default"}
                                        className={
                                          isBooked
                                            ? "bg-red-500 text-white"
                                            : "bg-green-500 text-white"
                                        }
                                      >
                                        {isBooked ? "Réservée" : "Libre"}
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-1">
                                      {table.capacity} pers.
                                    </p>
                                    {isBooked && reservation && (
                                      <p className="text-xs font-medium mt-2 truncate">
                                        {reservation.name}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Tables combinées */}
                          {combinedTablesStatus.length > 0 && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-2">Tables combinées</p>
                              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
                                {combinedTablesStatus.map(({ combinedTable, isBooked, reservation }) => (
                                  <div
                                    key={combinedTable.id}
                                    className={`p-3 rounded-lg border-2 transition-all ${
                                      isBooked
                                        ? "bg-red-50 border-red-300 dark:bg-red-950/30 dark:border-red-800"
                                        : "bg-green-50 border-green-300 dark:bg-green-950/30 dark:border-green-800"
                                    }`}
                                  >
                                     <div className="flex items-center justify-between mb-1">
                                      <span className="font-semibold text-sm">{combinedTable.name}</span>
                                      <Badge
                                        variant={isBooked ? "destructive" : "default"}
                                        className={
                                          isBooked
                                            ? "bg-red-500 text-white"
                                            : "bg-green-500 text-white"
                                        }
                                      >
                                        <span className="lg:hidden">{isBooked ? "Rés." : "Libre"}</span>
                                        <span className="hidden lg:inline">{isBooked ? "Réservée" : "Libre"}</span>
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-1">
                                      {combinedTable.totalCapacity} pers.
                                    </p>
                                    {isBooked && reservation && (
                                      <p className="text-xs font-medium mt-2 truncate">
                                        {reservation.name}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CollapsibleContent>
                      </Collapsible>
                    ) : (
                      <div className="mb-4 md:mb-6 p-3 md:p-4 bg-muted/30 rounded-lg">
                        <div className="flex items-center justify-between mb-2 md:mb-3">
                          <h4 className="text-xs md:text-sm font-semibold">État des tables</h4>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => setCompactTableView(!compactTableView)}
                                >
                                  {compactTableView ? (
                                    <LayoutList className="w-4 h-4" />
                                  ) : (
                                    <LayoutGrid className="w-4 h-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {compactTableView ? "Vue détaillée" : "Vue compacte"}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        
                        {compactTableView ? (
                          <CompactTableStatusView
                            tablesStatus={tablesStatus}
                            combinedTablesStatus={combinedTablesStatus}
                          />
                        ) : (
                          <>
                            {/* Tables simples */}
                            {tablesStatus.length > 0 && (
                              <div className="mb-3 md:mb-4">
                                <p className="text-xs text-muted-foreground mb-2">Tables individuelles</p>
                                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
                                  {tablesStatus.map(({ table, isBooked, reservation }) => (
                                    <div
                                      key={table.id}
                                      className={`p-2 md:p-3 rounded-lg border-2 transition-all text-xs md:text-sm ${
                                        isBooked
                                          ? "bg-red-50 border-red-300 dark:bg-red-950/30 dark:border-red-800"
                                          : "bg-green-50 border-green-300 dark:bg-green-950/30 dark:border-green-800"
                                      }`}
                                    >
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="font-semibold text-xs md:text-sm">{table.name}</span>
                                        <Badge
                                          variant={isBooked ? "destructive" : "default"}
                                          className={cn(
                                            "text-[10px] md:text-xs",
                                            isBooked
                                              ? "bg-red-500 text-white"
                                              : "bg-green-500 text-white"
                                          )}
                                        >
                                          <span className="lg:hidden">{isBooked ? "Rés." : "Libre"}</span>
                                          <span className="hidden lg:inline">{isBooked ? "Réservée" : "Libre"}</span>
                                        </Badge>
                                      </div>
                                      <p className="text-[10px] md:text-xs text-muted-foreground mb-1">
                                        {table.capacity} pers.
                                      </p>
                                      {isBooked && reservation && (
                                        <p className="text-[10px] md:text-xs font-medium mt-1 md:mt-2 truncate">
                                          {reservation.name}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Tables combinées */}
                            {combinedTablesStatus.length > 0 && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-2">Tables combinées</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                  {combinedTablesStatus.map(({ combinedTable, isBooked, reservation }) => (
                                    <div
                                      key={combinedTable.id}
                                      className={`p-3 rounded-lg border-2 transition-all ${
                                        isBooked
                                          ? "bg-red-50 border-red-300 dark:bg-red-950/30 dark:border-red-800"
                                          : "bg-green-50 border-green-300 dark:bg-green-950/30 dark:border-green-800"
                                      }`}
                                    >
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="font-semibold text-sm">{combinedTable.name}</span>
                                        <Badge
                                          variant={isBooked ? "destructive" : "default"}
                                          className={
                                            isBooked
                                              ? "bg-red-500 text-white"
                                              : "bg-green-500 text-white"
                                          }
                                        >
                                          <span className="lg:hidden">{isBooked ? "Rés." : "Libre"}</span>
                                          <span className="hidden lg:inline">{isBooked ? "Réservée" : "Libre"}</span>
                                        </Badge>
                                      </div>
                                      <p className="text-xs text-muted-foreground mb-1">
                                        {combinedTable.totalCapacity} pers.
                                      </p>
                                      {isBooked && reservation && (
                                        <p className="text-xs font-medium mt-2 truncate">
                                          {reservation.name}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}

                    {reservations.length > 0 ? (
                      <>
                        {!isMobile ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left py-2 px-1 md:px-2 font-semibold text-xs md:text-sm w-8"></th>
                                  <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Heure</th>
                                  <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Nom</th>
                                  <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Pers.</th>
                                  <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Table</th>
                                  <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm hidden lg:table-cell">Créée</th>
                                  <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Statut</th>
                                  <th className="text-left py-2 px-1.5 md:px-2 lg:px-3 font-semibold text-xs md:text-sm">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                          {reservations.map((reservation) => {
                            const status = getReservationStatus(reservation);
                            const hasTableProblem = hasInactiveOrMissingTable(
                              reservation, 
                              supabaseTables, 
                              supabaseCombinedTables
                            );
                            const hasCapacityProblem = hasCapacityExceeded(
                              reservation,
                              supabaseTables,
                              supabaseCombinedTables
                            );
                            const hasClosureProblem = hasClosureConflict(
                              reservation,
                              closures
                            );
                            
                            return (
                              <tr 
                                key={reservation.id} 
                                className={cn(
                                  "border-b hover:bg-muted/50",
                                  // Réservation arrivée - grisée
                                  reservation.status === "arrivé" && "opacity-50",
                                  // Réservation annulée - état passé, neutre
                                  reservation.status === "annulé" && "opacity-50 text-muted-foreground",
                                  // Problème de fermeture (priorité très haute - rouge)
                                  hasClosureProblem && reservation.status !== "arrivé" && "bg-red-50 dark:bg-red-950/20",
                                  // Problème de table (priorité haute - ambre)
                                  !hasClosureProblem && hasTableProblem && reservation.status !== "arrivé" && "bg-amber-50 dark:bg-amber-950/20",
                                  // Problème de capacité (priorité moyenne - bleu)
                                  !hasClosureProblem && !hasTableProblem && hasCapacityProblem && reservation.status !== "arrivé" && "bg-blue-50 dark:bg-blue-950/20"
                                )}
                              >
                                <td className="py-2 px-1 md:px-2">
                                  <div className="flex items-center gap-1">
                                    {hasClosureProblem && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                          </TooltipTrigger>
                                          <TooltipContent 
                                            className="bg-red-50 text-red-950 border-red-200 shadow-lg max-w-xs"
                                            side="right"
                                          >
                                            <p className="text-sm font-semibold">Réservation pendant une période de fermeture</p>
                                            <p className="text-xs mt-1">Cette réservation doit être traitée manuellement.</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {/* Note de réservation (amber bar) */}
                                    {reservation.notes && (
                                      <>
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div className="w-2 h-8 bg-amber-500 rounded-full cursor-help" />
                                            </TooltipTrigger>
                                            <TooltipContent 
                                              className="bg-amber-50 text-amber-950 border-amber-200 shadow-lg max-w-xs"
                                              side="right"
                                              sideOffset={5}
                                            >
                                              <p className="text-sm font-semibold mb-1">Note spéciale:</p>
                                              <p className="text-sm">{reservation.notes}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                        {reservation.email && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <button
                                                  onClick={() => {
                                                    setManualReplyTarget({
                                                      reservationId: reservation.supabaseId || '',
                                                      customerName: reservation.name,
                                                      customerEmail: reservation.email || '',
                                                      date: format(reservation.reservationDate, 'yyyy-MM-dd'),
                                                      time: reservation.time,
                                                      numberOfPeople: reservation.persons,
                                                      notes: reservation.notes,
                                                      establishmentName: establishmentName || undefined,
                                                    });
                                                    setManualReplyOpen(true);
                                                  }}
                                                  className="p-1 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded transition-colors"
                                                >
                                                  <Mail className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                                </button>
                                              </TooltipTrigger>
                                              <TooltipContent>Envoyer un email</TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                      </>
                                    )}
                                    {/* Note client persistante (violet icon) */}
                                    {(() => {
                                      const customerNotes = getCustomerNotes(reservation.email);
                                      if (!customerNotes) return null;
                                      return (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <StickyNote className="w-4 h-4 text-violet-500 cursor-help flex-shrink-0" />
                                            </TooltipTrigger>
                                            <TooltipContent 
                                              className="bg-violet-50 text-violet-950 border-violet-200 shadow-lg max-w-xs dark:bg-violet-950 dark:text-violet-100 dark:border-violet-800"
                                              side="right"
                                              sideOffset={5}
                                            >
                                              <p className="text-sm font-semibold mb-1">Note client:</p>
                                              <p className="text-sm">{customerNotes}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      );
                                    })()}
                                  </div>
                                </td>
                                <td className="py-2 px-1.5 md:px-2 lg:px-3 font-medium text-xs md:text-sm">{reservation.time}</td>
                                <td className="py-2 px-1.5 md:px-2 lg:px-3 text-xs md:text-sm">
                                  <div className="flex items-center">
                                    {reservation.notes ? (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className="cursor-help underline decoration-dotted decoration-muted-foreground/50">
                                              {reservation.name}
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent 
                                            className="bg-amber-50 text-amber-950 border-amber-200 shadow-lg max-w-xs"
                                            side="top"
                                            sideOffset={5}
                                          >
                                            <p className="text-sm">{reservation.notes}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    ) : (
                                      <span>{reservation.name}</span>
                                    )}
                                    {reservation.source === 'walk_in' && <Badge variant="warning" className="text-[10px] px-1.5 py-0 ml-1">Walk-in</Badge>}
                                    {getLoyaltyBadge(reservation.email)}
                                    {getNoShowBadge(reservation.email)}
                                  </div>
                                </td>
<td className="py-2 px-1.5 md:px-2 lg:px-3 text-xs md:text-sm">
                                   <span className="flex items-center gap-1">
                                    {reservation.persons + (reservation.numberOfChildren || 0)}
                                    {reservation.numberOfChildren > 0 && (
                                      <span className="flex items-center text-muted-foreground text-xs">
                                        <Baby className="w-3 h-3 mr-0.5" />
                                        dont {reservation.numberOfChildren}
                                      </span>
                                    )}
                                  </span>
                                </td>
                                <td className="py-2 px-1.5 md:px-2 lg:px-3 text-xs md:text-sm">
                                  <QuickTableChangeDialog
                                    reservation={{
                                      id: reservation.id,
                                      supabaseId: reservation.supabaseId,
                                      tableId: reservation.tableId,
                                      combinedTableId: reservation.combinedTableId,
                                      persons: reservation.persons,
                                      time: reservation.time,
                                      reservationDate: reservation.reservationDate,
                                    }}
                                    tables={supabaseTables}
                                    combinedTables={supabaseCombinedTables}
                                    allReservations={convertedReservations}
                                    availabilities={supabaseAvailabilities}
                                    weekdayAvailabilities={supabaseWeekdayAvailabilities}
                                    onTableChange={handleQuickTableChange}
                                    onSwapTables={async (a, b) => { await handleSwapTables(a, b); }}
                                    disabled={reservation.status === 'annulé'}
                                    reservationSettings={reservationSettings ? {
                                      bookingMode: reservationSettings.bookingMode as 'slot' | 'time',
                                      reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
                                    } : undefined}

                                  >
                                    <button
                                      className={cn(
                                        "flex items-center gap-1 px-2 py-1 rounded hover:bg-muted/80 transition-colors cursor-pointer group",
                                        hasTableProblem && "text-amber-700 dark:text-amber-400 font-semibold",
                                        !hasTableProblem && hasCapacityProblem && "text-blue-700 dark:text-blue-400 font-semibold",
                                        reservation.status === 'annulé' && "opacity-50 cursor-not-allowed"
                                      )}
                                      disabled={reservation.status === 'annulé'}
                                    >
                                      {reservation.tableId && (() => {
                                        const table = supabaseTables.find(t => t.id === reservation.tableId);
                                        if (!table) {
                                          return (
                                            <Badge variant="outline" className="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">
                                              Table supprimée
                                            </Badge>
                                          );
                                        }
                                        const isExceeded = hasCapacityProblem && reservation.persons > table.capacity;
                                        return (
                                          <span>
                                            {table.name}
                                            {isExceeded && (
                                              <span className="text-blue-600 dark:text-blue-400 font-semibold ml-1">
                                                ({reservation.persons}/{table.capacity})
                                              </span>
                                            )}
                                          </span>
                                        );
                                      })()}
                                      
                                      {reservation.combinedTableId && (() => {
                                        const combinedTable = supabaseCombinedTables.find(ct => ct.id === reservation.combinedTableId);
                                        if (!combinedTable) {
                                          return (
                                            <Badge variant="outline" className="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">
                                              Table combinée supprimée
                                            </Badge>
                                          );
                                        }
                                        const isExceeded = hasCapacityProblem && reservation.persons > combinedTable.totalCapacity;
                                        return (
                                          <span>
                                            {combinedTable.name}
                                            {isExceeded && (
                                              <span className="text-blue-600 dark:text-blue-400 font-semibold ml-1">
                                                ({reservation.persons}/{combinedTable.totalCapacity})
                                              </span>
                                            )}
                                          </span>
                                        );
                                      })()}
                                      
                                      {!reservation.tableId && !reservation.combinedTableId && (
                                        <Badge variant="outline" className="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">
                                          Aucune table
                                        </Badge>
                                      )}
                                      
                                      {reservation.status !== 'annulé' && (
                                        <ArrowRightLeft className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground ml-1" />
                                      )}
                                    </button>
                                  </QuickTableChangeDialog>
                                </td>
                                <td className="py-2 px-1.5 md:px-2 lg:px-3 text-xs text-muted-foreground hidden lg:table-cell">
                                  {format(reservation.createdAt, "d MMM", { locale: fr })}
                                </td>
                                <td className="py-2 px-1.5 md:px-2 lg:px-3">
                                  {status.type === "arrived" && (
                                    <Badge className="bg-green-500 text-white">
                                      <span className="lg:hidden">✓</span>
                                      <span className="hidden lg:inline">✓ {status.label}</span>
                                    </Badge>
                                  )}
                                  {status.type === "no-show" && (
                                    <Badge className="bg-gray-700 text-white">
                                      <span className="lg:hidden">NS</span>
                                      <span className="hidden lg:inline">No-show</span>
                                    </Badge>
                                  )}
                                  {status.type === "canceled" && (
                                    <Badge variant="outline" className="text-muted-foreground border-muted-foreground/30 bg-muted/50">
                                      <span className="lg:hidden">✕</span>
                                      <span className="hidden lg:inline">{status.label}</span>
                                    </Badge>
                                  )}
                                  {status.type === "scheduled" && (
                                    <Badge variant="secondary">
                                      <Clock className="w-3 h-3 lg:hidden" />
                                      <span className="hidden lg:inline">{status.label}</span>
                                    </Badge>
                                  )}
                                  {status.type === "on-time" && (
                                    <Badge className="bg-green-500 text-white">
                                      <Clock className="w-3 h-3 lg:hidden" />
                                      <span className="hidden lg:inline">{status.label}</span>
                                    </Badge>
                                  )}
                                  {status.type === "late" && (
                                    <Badge className="bg-orange-500 text-white">
                                      <span className="lg:hidden">{status.delay} mins</span>
                                      <span className="hidden lg:inline">{status.label} +{status.delay} min</span>
                                    </Badge>
                                  )}
                                  {status.type === "very-late" && (
                                    <Badge variant="destructive">
                                      <span className="lg:hidden">{status.delay} mins</span>
                                      <span className="hidden lg:inline">{status.label} +{status.delay} min</span>
                                    </Badge>
                                  )}
                                  {status.type === "no-show-probable" && (
                                    <Badge variant="destructive" className="bg-gray-700">
                                      <span className="lg:hidden">{status.delay} mins</span>
                                      <span className="hidden lg:inline">{status.label} +{status.delay} min</span>
                                    </Badge>
                                  )}
                                </td>
                                 <td className="py-2 px-1.5 md:px-2 lg:px-3">
                                  <div className="flex items-center gap-1">
                                    {/* Status buttons - always visible */}
                                    {status.type !== "arrived" && status.type !== "no-show" && (
                                      <Button
                                        size="icon"
                                        variant="outline"
                                        className="h-8 w-8"
                                        onClick={() => handleUpdateReservationStatus(reservation.id, "arrivé")}
                                        title="Marquer comme arrivé"
                                      >
                                        <Check className="w-4 h-4" />
                                      </Button>
                                    )}
                                    {status.type !== "canceled" && status.type !== "no-show" && (
                                      <Button
                                        size="icon"
                                        variant="outline"
                                        className="h-8 w-8"
                                        onClick={() => handleUpdateReservationStatus(reservation.id, "annulé")}
                                        title="Annuler"
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    )}
                                    {(status.type === "late" || status.type === "very-late" || status.type === "no-show-probable") && (
                                      <Button
                                        size="icon"
                                        variant="outline"
                                        className="h-8 w-8 text-gray-700 border-gray-400 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                                        onClick={() => handleUpdateReservationStatus(reservation.id, "annulé", "no-show")}
                                        title="Marquer no-show"
                                      >
                                        <UserX className="w-4 h-4" />
                                      </Button>
                                    )}
                                    {(status.type === "arrived" || status.type === "canceled" || status.type === "no-show") && (
                                      <Button
                                        size="icon"
                                        variant="outline"
                                        className="h-8 w-8"
                                        onClick={() => handleUpdateReservationStatus(reservation.id, "planifié")}
                                        title="Marquer comme planifié"
                                      >
                                        <Clock className="w-4 h-4" />
                                      </Button>
                                    )}
                                    {/* Secondary actions dropdown */}
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="outline" size="icon" className="h-8 w-8">
                                          <MoreVertical className="w-4 h-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end" className="w-52 bg-background z-50 border shadow-lg">
                                        <DropdownMenuItem onClick={() => setEditingReservation(reservation)}>
                                          <Edit className="w-4 h-4 mr-2" />
                                          Modifier
                                        </DropdownMenuItem>
                                        {reservation.email && reservation.status === "annulé" && (
                                          <DropdownMenuItem
                                            onClick={() => {
                                              setEmailToSend({
                                                reservationId: reservation.id,
                                                reservationUuid: reservation.supabaseId,
                                                emailType: 'cancellation',
                                                reservationName: reservation.name,
                                                reservationEmail: reservation.email!
                                              });
                                              setEmailDialogOpen(true);
                                            }}
                                            disabled={sendingEmail === reservation.id}
                                          >
                                            <Mail className="w-4 h-4 mr-2" />
                                            Email d'annulation
                                          </DropdownMenuItem>
                                        )}
                                        {reservation.email && reservation.status === "planifié" && (
                                          <>
                                            <DropdownMenuItem
                                              onClick={() => {
                                                setEmailToSend({
                                                  reservationId: reservation.id,
                                                  reservationUuid: reservation.supabaseId,
                                                  emailType: 'confirmation',
                                                  reservationName: reservation.name,
                                                  reservationEmail: reservation.email!
                                                });
                                                setEmailDialogOpen(true);
                                              }}
                                              disabled={sendingEmail === reservation.id}
                                            >
                                              <Mail className="w-4 h-4 mr-2" />
                                              Email de confirmation
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                              onClick={() => {
                                                setEmailToSend({
                                                  reservationId: reservation.id,
                                                  reservationUuid: reservation.supabaseId,
                                                  emailType: 'modification',
                                                  reservationName: reservation.name,
                                                  reservationEmail: reservation.email!
                                                });
                                                setEmailDialogOpen(true);
                                              }}
                                              disabled={sendingEmail === reservation.id}
                                            >
                                              <Mail className="w-4 h-4 mr-2" />
                                              Email de modification
                                            </DropdownMenuItem>
                                          </>
                                        )}
                                        {reservation.email && reservation.status === "arrivé" && (
                                          <DropdownMenuItem
                                            onClick={() => {
                                              setEmailToSend({
                                                reservationId: reservation.id,
                                                reservationUuid: reservation.supabaseId,
                                                emailType: 'modification',
                                                reservationName: reservation.name,
                                                reservationEmail: reservation.email!
                                              });
                                              setEmailDialogOpen(true);
                                            }}
                                            disabled={sendingEmail === reservation.id}
                                          >
                                            <Mail className="w-4 h-4 mr-2" />
                                            Email de modification
                                          </DropdownMenuItem>
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {reservations.map((reservation) => {
                              const status = getReservationStatus(reservation);
                              const hasTableProblem = hasInactiveOrMissingTable(
                                reservation, 
                                supabaseTables, 
                                supabaseCombinedTables
                              );
                              const hasCapacityProblem = hasCapacityExceeded(
                                reservation,
                                supabaseTables,
                                supabaseCombinedTables
                              );
                              
                              return (
                                <Card 
                                  key={reservation.id}
                                  id={`reservation-${reservation.id}`}
                                  className={cn(
                                    "p-3 space-y-2 transition-all duration-300",
                                    // Réservation arrivée - grisée
                                    reservation.status === "arrivé" && "opacity-50",
                                    // Réservation annulée - état passé, neutre
                                    reservation.status === "annulé" && "opacity-50 text-muted-foreground",
                                    hasTableProblem && reservation.status !== "arrivé" && "bg-amber-50 dark:bg-amber-950/20",
                                    !hasTableProblem && hasCapacityProblem && reservation.status !== "arrivé" && "bg-blue-50 dark:bg-blue-950/20"
                                  )}
                                >
                                  {/* Header : Prénom + Statut */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-semibold">
                                        {reservation.name.split(' ')[0]}
                                      </span>
                                      {reservation.source === 'walk_in' && <Badge variant="warning" className="text-[10px] px-1.5 py-0">Walk-in</Badge>}
                                      {getLoyaltyBadge(reservation.email)}
                                    </div>
                                    {getStatusBadge(reservation.status, reservation.cancellationReason)}
                                  </div>
                                  
                                  {/* Info supplémentaire compacte */}
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                                    <div className="flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      <span>{reservation.persons + (reservation.numberOfChildren || 0)} pers.</span>
                                      {reservation.numberOfChildren > 0 && (
                                        <span className="flex items-center text-muted-foreground text-xs">
                                          <Baby className="w-3 h-3 mr-0.5" />
                                          dont {reservation.numberOfChildren}
                                        </span>
                                      )}
                                    </div>
                                    <span>•</span>
                                    <span>{reservation.time}</span>
                                    {reservation.tableId && (() => {
                                      const table = supabaseTables.find(t => t.id === reservation.tableId);
                                      if (table) {
                                        return (
                                          <>
                                            <span>•</span>
                                            <span>{table.name}</span>
                                          </>
                                        );
                                      }
                                    })()}
                                    {reservation.combinedTableId && (() => {
                                      const combinedTable = supabaseCombinedTables.find(ct => ct.id === reservation.combinedTableId);
                                      if (combinedTable) {
                                        return (
                                          <>
                                            <span>•</span>
                                            <span>{combinedTable.name}</span>
                                          </>
                                        );
                                      }
                                    })()}
                                  </div>

                                  {/* Note spéciale du client - mise en évidence orange */}
                                  {reservation.notes && (
                                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-2">
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1">
                                          <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">
                                            Note spéciale:
                                          </p>
                                          <p className="text-xs text-amber-900 dark:text-amber-200">
                                            {reservation.notes}
                                          </p>
                                        </div>
                                        {reservation.email && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                                            onClick={() => {
                                              setManualReplyTarget({
                                                reservationId: reservation.supabaseId || '',
                                                customerName: reservation.name,
                                                customerEmail: reservation.email || '',
                                                date: format(reservation.reservationDate, 'yyyy-MM-dd'),
                                                time: reservation.time,
                                                numberOfPeople: reservation.persons,
                                                notes: reservation.notes,
                                                establishmentName: establishmentName || undefined,
                                              });
                                              setManualReplyOpen(true);
                                            }}
                                          >
                                            <Mail className="w-4 h-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Bouton Actions unique */}
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="outline" size="sm" className="w-full active:scale-95 transition-transform">
                                        Actions
                                        <ChevronDown className="w-4 h-4 ml-2" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                                      {status.type !== "arrived" && status.type !== "no-show" && (
                                        <DropdownMenuItem onClick={() => handleUpdateReservationStatus(reservation.id, "arrivé")}>
                                          <Check className="w-4 h-4 mr-2" />
                                          Marquer arrivé
                                        </DropdownMenuItem>
                                      )}
                                      {status.type !== "canceled" && status.type !== "no-show" && (
                                        <DropdownMenuItem onClick={() => handleUpdateReservationStatus(reservation.id, "annulé")}>
                                          <X className="w-4 h-4 mr-2" />
                                          Annuler
                                        </DropdownMenuItem>
                                      )}
                                      {(status.type === "late" || status.type === "very-late" || status.type === "no-show-probable") && (
                                        <DropdownMenuItem onClick={() => handleUpdateReservationStatus(reservation.id, "annulé", "no-show")}>
                                          <UserX className="w-4 h-4 mr-2" />
                                          No-show
                                        </DropdownMenuItem>
                                      )}
                                      {(status.type === "arrived" || status.type === "canceled" || status.type === "no-show") && (
                                        <DropdownMenuItem onClick={() => handleUpdateReservationStatus(reservation.id, "planifié")}>
                                          <Clock className="w-4 h-4 mr-2" />
                                          Replanifier
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuItem onClick={() => setEditingReservation(reservation)}>
                                        <Edit className="w-4 h-4 mr-2" />
                                        Modifier
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => window.location.href = `tel:${reservation.phone}`}>
                                        <Phone className="w-4 h-4 mr-2" />
                                        Appeler
                                      </DropdownMenuItem>
                                      {reservation.email && (
                                        <DropdownMenuItem 
                                          onClick={() => {
                                            setEmailToSend({
                                              reservationId: reservation.id,
                                              reservationUuid: reservation.supabaseId,
                                              emailType: reservation.status === "annulé" ? 'cancellation' : 'confirmation',
                                              reservationName: reservation.name,
                                              reservationEmail: reservation.email!
                                            });
                                            setEmailDialogOpen(true);
                                          }}
                                        >
                                          <Mail className="w-4 h-4 mr-2" />
                                          Envoyer email
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </Card>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        Aucune réservation pour ce créneau
                      </div>
                    )}
                  </>
                )}
              </Card>
            );
          })}
        </div>
        )}
          </TabsContent>

          <TabsContent value="salle" className="m-0">
            <FloorPlanView
              establishmentId={establishmentId || undefined}
              tables={supabaseTables}
              combinedTables={supabaseCombinedTables}
              reservations={convertedReservations}
              selectedDate={selectedDate}
              timeSlots={timeSlots}
              availabilities={supabaseAvailabilities}
              weekdayAvailabilities={supabaseWeekdayAvailabilities}
              onDateChange={setSelectedDate}
              onCreateAtTable={(tableId, time, date) => {
                setSelectedDate(date);
                setFloorPlanCreate({ tableId, time, date });
              }}
              onEditReservation={(reservation) => {
                const dashboardRes = reservation.supabaseId
                  ? reservations.find(r => r.supabaseId === reservation.supabaseId)
                  : reservations.find(r => 
                      r.tableId === reservation.tableId && 
                      r.time === reservation.startTime.substring(0, 5) &&
                      r.name === ([reservation.customerFirstName, reservation.customerLastName].filter(Boolean).join(' ') || 'Client')
                    );
                if (dashboardRes) {
                  setEditingReservation(dashboardRes);
                } else {
                  toast({ title: "Erreur", description: "Réservation introuvable.", variant: "destructive" });
                }
              }}
              onPreviewReservation={(reservation) => {
                const dashboardRes = reservation.supabaseId
                  ? reservations.find(r => r.supabaseId === reservation.supabaseId)
                  : reservations.find(r => 
                      r.tableId === reservation.tableId && 
                      r.time === reservation.startTime.substring(0, 5) &&
                      r.name === ([reservation.customerFirstName, reservation.customerLastName].filter(Boolean).join(' ') || 'Client')
                    );
                if (dashboardRes) {
                  setPreviewReservation(dashboardRes);
                } else {
                  toast({ title: "Erreur", description: "Réservation introuvable.", variant: "destructive" });
                }
              }}
              onStatusChange={async (reservation, status) => {
                const dashboardRes = reservation.supabaseId
                  ? reservations.find(r => r.supabaseId === reservation.supabaseId)
                  : reservations.find(r => 
                      r.tableId === reservation.tableId && 
                      r.time === reservation.startTime.substring(0, 5) &&
                      r.name === ([reservation.customerFirstName, reservation.customerLastName].filter(Boolean).join(' ') || 'Client')
                    );
                if (dashboardRes) {
                  await handleUpdateReservationStatus(dashboardRes.id, status);
                } else {
                  toast({ title: "Erreur", description: "Réservation introuvable.", variant: "destructive" });
                }
              }}
              onTableChange={async (reservationId, tableId, combinedTableId) => {
                const localRes = reservations.find(r => r.id.toString() === reservationId);
                const supabaseId = localRes?.supabaseId;

                if (!supabaseId) {
                  toast({
                    title: "Erreur",
                    description: "Réservation introuvable (impossible de la déplacer).",
                    variant: "destructive",
                  });
                  return;
                }

                try {
                  const { error } = await supabase
                    .from('reservations')
                    .update({
                      table_id: tableId,
                      combined_table_id: combinedTableId,
                    })
                    .eq('id', supabaseId);

                  if (error) throw error;

                  toast({
                    title: "Table modifiée",
                    description: "La réservation a été déplacée.",
                  });

                  refetch();
                } catch (error) {
                  if (isPhysicalOverlapError(error)) {
                    const { title, description } = getConflictToastMessage();
                    toast({ title, description, variant: "destructive" });
                  } else {
                    toast({
                      title: "Erreur",
                      description: "Impossible de changer la table.",
                      variant: "destructive",
                    });
                  }
                }
              }}
              waitlistReservations={waitlistReservations}
              onWaitlistAssignTable={(reservationId) => {
                const r = reservations.find(res => res.id === reservationId);
                if (r) setWaitlistAssigning(r);
              }}
              onWaitlistCancel={(reservationId) => {
                handleUpdateReservationStatus(reservationId, "annulé");
              }}
              onWaitlistEdit={(reservationId) => {
                const r = reservations.find(res => res.id === reservationId);
                if (r) setEditingReservation(r);
              }}
              onWaitlistMarkArrived={(reservationId) => {
                handleUpdateReservationStatus(reservationId, "arrivé");
              }}
              onOpenAvailability={() => setAvailabilityOpen(true)}
              onOpenWalkIn={() => setWalkInOpen(true)}
              onLockChange={async (reservationId, locked) => {
                const localRes = reservations.find(r => r.id.toString() === reservationId);
                const supabaseId = localRes?.supabaseId;
                if (!supabaseId) {
                  toast({
                    title: "Erreur",
                    description: "Réservation introuvable.",
                    variant: "destructive",
                  });
                  return;
                }
                try {
                  const { error } = await supabase
                    .from('reservations')
                    .update({ is_locked: locked })
                    .eq('id', supabaseId);
                  if (error) throw error;
                  toast({
                    title: locked ? "Réservation verrouillée" : "Réservation déverrouillée",
                    description: locked
                      ? "Le drag-and-drop sur le plan de salle est bloqué."
                      : "Le drag-and-drop est de nouveau autorisé.",
                  });
                  refetch();
                } catch (e: any) {
                  toast({
                    title: "Erreur",
                    description: e?.message ?? "Impossible de modifier le verrou.",
                    variant: "destructive",
                  });
                }
              }}
              onSwapTables={async (aId, bId) => {
                const a = reservations.find(r => r.id.toString() === aId);
                const b = reservations.find(r => r.id.toString() === bId);
                const aSup = a?.supabaseId;
                const bSup = b?.supabaseId;
                if (!aSup || !bSup) {
                  toast({
                    title: "Erreur",
                    description: "Réservation introuvable.",
                    variant: "destructive",
                  });
                  throw new Error("Réservation introuvable");
                }
                await handleSwapTables(aSup, bSup);
              }}
            />
          </TabsContent>

          <TabsContent value="recherche">
            <ReservationSearch 
              reservations={reservations}
              allTables={supabaseTables}
              combinedTables={supabaseCombinedTables}
              onEdit={(reservation) => setEditingReservation(reservation as any)}
              onUpdateStatus={handleUpdateReservationStatus}
              onQuickTableChange={handleQuickTableChange}
              onSwapTables={async (a, b) => { await handleSwapTables(a, b); }}

              onSendEmail={(data) => {
                setEmailToSend(data);
                setEmailDialogOpen(true);
              }}
              sendingEmail={sendingEmail}
              getReservationStatus={getReservationStatus}
              getLoyaltyBadge={getLoyaltyBadge}
              getNoShowBadge={getNoShowBadge}
              getCustomerNotes={getCustomerNotes}
              convertedReservations={convertedReservations}
              availabilities={supabaseAvailabilities}
              weekdayAvailabilities={supabaseWeekdayAvailabilities}
              reservationSettings={reservationSettings ? {
                bookingMode: reservationSettings.bookingMode as 'slot' | 'time',
                reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
              } : undefined}
            />
          </TabsContent>

          <TabsContent value="clients">
            <ClientsView 
              reservations={convertedReservations}
              onReservationsUpdate={(updatedReservations) => {
                // Update the original reservations state based on the updated converted reservations
                setReservations(prevReservations => 
                  prevReservations.map(res => {
                    const updated = updatedReservations.find(r => r.id === res.id.toString());
                    if (updated) {
                      return {
                        ...res,
                        name: [updated.customerFirstName, updated.customerLastName].filter(Boolean).join(' ') || 'Client',
                        email: updated.customerEmail,
                        phone: updated.customerPhone,
                      };
                    }
                    return res;
                  })
                );
              }}
              timeSlots={timeSlots}
              allTables={supabaseTables}
              allReservations={reservations}
              availabilities={supabaseAvailabilities}
              weekdayAvailabilities={supabaseWeekdayAvailabilities}
              combinedTables={supabaseCombinedTables}
              onReservationUpdate={handleUpdateReservation}
            />
          </TabsContent>

          {reservationSettings?.terraceEnabled && establishmentId && (
            <TabsContent value="terrasse">
              <TerraceManagementTab establishmentId={establishmentId} reservations={supabaseReservations} tables={supabaseTables} combinedTables={supabaseCombinedTables} timeSlots={supabaseTimeSlots} availabilities={supabaseAvailabilities} weekdayAvailabilities={supabaseWeekdayAvailabilities} bookablePeriods={supabaseBookablePeriods} childrenCapacityMode={(reservationSettings?.childrenCapacityMode as 'full' | 'half' | 'none') ?? 'full'} />
            </TabsContent>
          )}
        </Tabs>

        {/* Global Reservation Preview Dialog - click opens here, "Modifier" forwards to edit */}
        {(() => {
          if (!previewReservation) return null;
          // Re-derive from the live list so status changes / updates are reflected immediately.
          const live = reservations.find(r => r.id === previewReservation.id);
          if (!live) {
            // Reservation disappeared (deleted) — close the dialog on next tick.
            queueMicrotask(() => setPreviewReservation(null));
            return null;
          }
          const duration = live.durationMinutes ?? reservationSettings?.reservationDurationMinutes ?? 120;
          const [hh, mm] = live.time.split(':').map(Number);
          const total = hh * 60 + mm + duration;
          const endTime = `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
          return (
            <ReservationPreviewDialog
              open={!!previewReservation}
              onOpenChange={(open) => { if (!open) setPreviewReservation(null); }}
              reservation={live}
              endTime={endTime}
              tables={supabaseTables}
              combinedTables={supabaseCombinedTables}
              convertedReservations={convertedReservations}
              availabilities={supabaseAvailabilities}
              weekdayAvailabilities={supabaseWeekdayAvailabilities}
              getReservationStatus={getReservationStatus}
              getLoyaltyBadge={getLoyaltyBadge}
              getNoShowBadge={getNoShowBadge}
              getCustomerNotes={getCustomerNotes}
              onUpdateStatus={handleUpdateReservationStatus}
              onEdit={(res) => setEditingReservation(res as any)}
              onQuickTableChange={handleQuickTableChange}
              onSendEmail={(data) => {
                setEmailToSend(data);
                setEmailDialogOpen(true);
              }}
              sendingEmail={sendingEmail}
              onSwapTables={handleSwapTables}
              reservationSettings={reservationSettings ? {
                bookingMode: reservationSettings.bookingMode as 'slot' | 'time',
                reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
              } : undefined}
            />
          );
        })()}

        {/* Global Edit Reservation Dialog - accessible from all tabs */}
        <EditReservationDialog
          open={!!editingReservation}
          onOpenChange={(open) => { if (!open) setEditingReservation(null); }}
          reservation={editingReservation}
          timeSlots={supabaseTimeSlots.map(ts => ({ start: ts.start.substring(0, 5), end: ts.end.substring(0, 5), availableDays: ts.availableDays, durationOverrideMinutes: ts.durationOverrideMinutes }))}
          dateOverrides={supabaseDateOverrides}
          allTables={supabaseTables.filter(t => t.isActive)}
          allReservations={reservations}
          availabilities={supabaseAvailabilities}
          weekdayAvailabilities={supabaseWeekdayAvailabilities}
          combinedTables={supabaseCombinedTables}
          existingClients={existingClients}
          onReservationUpdate={(reservationId, updates) => {
            handleUpdateReservation(reservationId, updates);
            setEditingReservation(null);
          }}
          onClientUpdate={handleClientUpdate}
          reservationSettings={reservationSettings ? {
            bookingMode: reservationSettings.bookingMode as 'slot' | 'time',
            bookingIntervalMinutes: reservationSettings.bookingIntervalMinutes,
            reservationDurationMinutes: reservationSettings.reservationDurationMinutes
          } : undefined}
          maxPeople={reservationSettings?.maxPeople}
          childrenEnabled={reservationSettings?.childrenEnabled}
          strollerEnabled={reservationSettings?.strollerEnabled}
          childrenCapacityMode={reservationSettings?.childrenCapacityMode}
          terraceEnabled={reservationSettings?.terraceEnabled}
          tableBookablePeriods={supabaseBookablePeriods}
          establishmentClosures={closures}
        />

        {/* Email Confirmation Dialog */}
        <AlertDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Confirmation d'envoi d'email
              </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="font-semibold text-foreground">
                  ⚠️ Vous êtes sur le point d&apos;envoyer un email de {
                    emailToSend?.emailType === 'cancellation' 
                      ? "confirmation d'annulation" 
                      : emailToSend?.emailType === 'confirmation'
                      ? "confirmation"
                      : "modification"
                  }.
                </p>
                {emailToSend && (
                  <div className="bg-muted p-3 rounded-lg space-y-1 text-sm">
                    <p><strong>Client :</strong> {emailToSend.reservationName}</p>
                    <p><strong>Email :</strong> {emailToSend.reservationEmail}</p>
                    <p><strong>Type :</strong> {
                      emailToSend.emailType === 'cancellation' 
                        ? "Annulation" 
                        : emailToSend.emailType === 'confirmation'
                        ? "Confirmation"
                        : "Modification"
                    }</p>
                  </div>
                )}
                <p className="text-muted-foreground text-xs">
                  Le client recevra un email automatique avec les détails de sa réservation.
                </p>
              </div>
            </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setEmailToSend(null)}>
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleSendManualEmail}>
                Confirmer l'envoi
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Dialog pour proposer l'envoi d'email après création */}
        <AlertDialog open={showEmailConfirmDialog} onOpenChange={setShowEmailConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Envoyer un email de confirmation ?
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 pt-2">
                  <p className="text-foreground">
                    Souhaitez-vous envoyer un email de confirmation à <strong>{newReservationForEmail?.customerName}</strong> ?
                  </p>
                  {newReservationForEmail && (
                    <div className="bg-muted p-3 rounded-lg space-y-1 text-sm">
                      <p><strong>Email :</strong> {newReservationForEmail.customerEmail}</p>
                    </div>
                  )}
                  <p className="text-muted-foreground text-xs">
                    Le client recevra un email avec les détails de sa réservation et un lien pour la modifier ou l&apos;annuler.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleSkipNewReservationEmail}>
                Non, pas maintenant
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleSendNewReservationEmail}
                disabled={sendingEmail === newReservationForEmail?.reservationId}
              >
                {sendingEmail === newReservationForEmail?.reservationId ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Oui, envoyer l'email
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>



        {/* Popup établissement inactif */}
        <Dialog open={showInactiveDialog} onOpenChange={setShowInactiveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <DialogTitle>Établissement désactivé</DialogTitle>
              </div>
              <DialogDescription className="pt-4 space-y-3">
                <p>
                  Votre établissement est actuellement <strong>désactivé</strong>.
                </p>
                <p>
                  Les réservations en ligne sont suspendues et la page de réservation publique n'est plus accessible.
                </p>
                <p className="text-sm text-muted-foreground">
                  Pour réactiver votre établissement, veuillez contacter l'administrateur Piktable.
                </p>
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end pt-4">
              <Button onClick={() => setShowInactiveDialog(false)}>
                Compris
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialogue de mise en pause des réservations */}
        <PauseReservationsDialog
          open={showPauseDialog}
          onOpenChange={setShowPauseDialog}
          currentMessage={reservationSettings?.isPaused ? reservationSettings?.pauseMessage || null : null}
          savedMessage={reservationSettings?.pauseMessage || null}
          onConfirm={handlePauseWithMessage}
        />
        
        {/* Print Reservations Dialog */}
        <PrintReservationsDialog
          open={showPrintDialog}
          onOpenChange={setShowPrintDialog}
          date={selectedDate}
          reservations={reservations}
          tables={supabaseTables}
          combinedTables={supabaseCombinedTables}
          timeSlots={timeSlots}
          establishmentName={establishmentName || ""}
        />
        
        {/* Feedback Dialog */}
        <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
        <PWAInstallDialog open={showPWADialog} onOpenChange={setShowPWADialog} />

        <ManualReplyDialog
          open={manualReplyOpen}
          onOpenChange={(o) => {
            setManualReplyOpen(o);
            if (!o) setManualReplyTarget(null);
          }}
          target={manualReplyTarget}
        />


        
        {/* AI Chat Dialog */}
        <AIChatDialog open={aiChatOpen} onOpenChange={setAiChatOpen} />

        {/* Walk-in Dialog */}
        <WalkInDialog
          open={walkInOpen}
          onOpenChange={setWalkInOpen}
          tables={supabaseTables}
          combinedTables={supabaseCombinedTables}
          reservations={convertedReservations}
          selectedDate={selectedDate}
          existingClients={existingClients}
          reservationDurationMinutes={reservationSettings?.reservationDurationMinutes ?? 120}
          bookingMode={(reservationSettings?.bookingMode as 'slot' | 'time') ?? 'slot'}
          timeSlots={timeSlots}
          reservationSettings={reservationSettings ? {
            childrenEnabled: reservationSettings.childrenEnabled,
            childrenCapacityMode: reservationSettings.childrenCapacityMode,
            strollerEnabled: reservationSettings.strollerEnabled,
          } : undefined}
          onCreateReservation={async (data) => {
            await handleCreateReservation(data);
            // After creation, mark as arrived for "seat now" walk-ins (has a table)
            if (data.tableId || data.combinedTableId) {
              // Small delay to let refetch complete
              setTimeout(async () => {
                const latest = supabaseReservations;
                // Find the just-created reservation by matching criteria
                const created = latest.find(r =>
                  r.source === 'walk_in' &&
                  r.status === 'planifié' &&
                  format(r.date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') &&
                  (r.tableId === data.tableId || r.combinedTableId === data.combinedTableId)
                );
                if (created) {
                  await supabase.functions.invoke('update-reservation-status', {
                    body: { reservationId: created.id, status: 'arrivé' }
                  });
                  refetch();
                }
              }, 1500);
            }
          }}
        />

        {/* Retroactive Reservation Dialog (manager) — mode complet, toggle pré-activé */}
        <NewReservationDialog
          open={retroactiveDialogOpen}
          onOpenChange={setRetroactiveDialogOpen}
          timeSlot=""
          date={new Date(Date.now() - 24 * 60 * 60 * 1000)}
          availableTables={supabaseTables.filter(t => t.isActive && t.isBookable !== false)}
          combinedTables={supabaseCombinedTables}
          allReservations={supabaseReservations}
          existingClients={existingClients}
          availabilities={supabaseAvailabilities}
          weekdayAvailabilities={supabaseWeekdayAvailabilities}
          onReservationCreate={handleCreateReservation}
          readOnlyMode={false}
          defaultRetroactive={true}
          establishmentId={establishmentId || undefined}
          maxPeople={reservationSettings?.maxPeople}
          childrenEnabled={reservationSettings?.childrenEnabled}
          strollerEnabled={reservationSettings?.strollerEnabled}
          childrenCapacityMode={reservationSettings?.childrenCapacityMode as 'full' | 'half' | 'none' | undefined}
          terraceEnabled={reservationSettings?.terraceEnabled}
          bookablePeriods={supabaseBookablePeriods}
          reservationSettings={reservationSettings ? {
            bookingMode: (reservationSettings.bookingMode as 'slot' | 'time') ?? 'slot',
            reservationDurationMinutes: reservationSettings.reservationDurationMinutes ?? 120,
            bookingIntervalMinutes: (reservationSettings.bookingIntervalMinutes as 15 | 30 | 60) ?? 30,
          } : undefined}
        />

        {/* Manager Override Dialog — créneau/jour fermé, table non-réservable */}
        <NewReservationDialog
          open={forceOverrideDialogOpen}
          onOpenChange={setForceOverrideDialogOpen}
          timeSlot=""
          date={new Date()}
          availableTables={supabaseTables.filter(t => t.isActive)}
          combinedTables={supabaseCombinedTables}
          allReservations={supabaseReservations}
          existingClients={existingClients}
          availabilities={supabaseAvailabilities}
          weekdayAvailabilities={supabaseWeekdayAvailabilities}
          onReservationCreate={handleCreateReservation}
          readOnlyMode={false}
          defaultForceOverride={true}
          establishmentId={establishmentId || undefined}
          maxPeople={reservationSettings?.maxPeople}
          childrenEnabled={reservationSettings?.childrenEnabled}
          strollerEnabled={reservationSettings?.strollerEnabled}
          childrenCapacityMode={reservationSettings?.childrenCapacityMode as 'full' | 'half' | 'none' | undefined}
          terraceEnabled={reservationSettings?.terraceEnabled}
          bookablePeriods={supabaseBookablePeriods}
          reservationSettings={reservationSettings ? {
            bookingMode: (reservationSettings.bookingMode as 'slot' | 'time') ?? 'slot',
            reservationDurationMinutes: reservationSettings.reservationDurationMinutes ?? 120,
            bookingIntervalMinutes: (reservationSettings.bookingIntervalMinutes as 15 | 30 | 60) ?? 30,
          } : undefined}
        />



        {/* Availability Dialog */}
        <AvailabilityDialog
          open={availabilityOpen}
          onOpenChange={setAvailabilityOpen}
          tables={supabaseTables}
          combinedTables={supabaseCombinedTables}
          reservations={convertedReservations}
          availabilities={supabaseAvailabilities}
          weekdayAvailabilities={supabaseWeekdayAvailabilities}
          bookablePeriods={supabaseBookablePeriods}
          timeSlots={timeSlots}
          allTimeSlots={supabaseTimeSlots.map(ts => ({
            id: ts.id,
            start: ts.start,
            end: ts.end,
            availableDays: ts.availableDays,
          }))}
          dateOverrides={supabaseDateOverrides}
          bookingMode={(reservationSettings?.bookingMode as 'slot' | 'time') ?? 'slot'}
          reservationDurationMinutes={reservationSettings?.reservationDurationMinutes ?? 120}
          reservationSettings={{
            childrenEnabled: reservationSettings?.childrenEnabled ?? false,
            strollerEnabled: reservationSettings?.strollerEnabled ?? false,
            childrenCapacityMode: reservationSettings?.childrenCapacityMode ?? 'full',
            terraceEnabled: reservationSettings?.terraceEnabled ?? false,
            bookingIntervalMinutes: (reservationSettings as any)?.bookingIntervalMinutes,
            minPeople: reservationSettings?.minPeople ?? 1,
          }}
          establishmentClosures={closures?.map(c => ({
            startDate: typeof c.start_date === 'string' ? c.start_date : format(c.start_date, 'yyyy-MM-dd'),
            endDate: typeof c.end_date === 'string' ? c.end_date : format(c.end_date, 'yyyy-MM-dd'),
          }))}
          isSlotClosedOnDate={isSlotClosedOnDate}
          isTerraceClosedOnDate={isTerraceClosedOnDate}
          existingClients={existingClients}
          onReservationCreate={handleCreateReservation}
          establishmentId={establishmentId}
          maxPeople={reservationSettings?.maxPeople}
          customAssignmentOrderEnabled={reservationSettings?.customAssignmentOrderEnabled ?? false}
        />

        {/* Floor plan v2 — création depuis clic table libre */}
        {floorPlanCreate && (() => {
          const tbl = supabaseTables.find(t => t.id === floorPlanCreate.tableId);
          if (!tbl) return null;
          return (
            <QuickReservationDialog
              open={true}
              onOpenChange={(o) => { if (!o) setFloorPlanCreate(null); }}
              preselectedTable={tbl}
              preselectedTime={floorPlanCreate.time}
              preselectedDate={floorPlanCreate.date}
              timeSlots={timeSlots}
              existingClients={existingClients}
              onReservationCreate={async (r) => {
                await handleCreateReservation(r);
                setFloorPlanCreate(null);
              }}
              reservationSettings={reservationSettings ? {
                bookingMode: (reservationSettings.bookingMode as 'slot' | 'time') ?? 'slot',
                reservationDurationMinutes: reservationSettings.reservationDurationMinutes ?? 120,
                childrenEnabled: reservationSettings.childrenEnabled,
                strollerEnabled: reservationSettings.strollerEnabled,
                minPeople: (reservationSettings as any).minPeople,
              } : undefined}
            />
          );
        })()}

        <WaitlistAssignTableDialog
          open={!!waitlistAssigning}
          onOpenChange={(open) => { if (!open) setWaitlistAssigning(null); }}
          reservation={waitlistAssigning ? {
            id: waitlistAssigning.id,
            supabaseId: waitlistAssigning.supabaseId,
            name: waitlistAssigning.name,
            persons: waitlistAssigning.persons,
            numberOfChildren: waitlistAssigning.numberOfChildren,
            hasStroller: waitlistAssigning.hasStroller,
            time: waitlistAssigning.time,
            reservationDate: waitlistAssigning.reservationDate,
          } : null}
          tables={supabaseTables}
          combinedTables={supabaseCombinedTables}
          allReservations={convertedReservations}
          availabilities={supabaseAvailabilities}
          weekdayAvailabilities={supabaseWeekdayAvailabilities}
          onAssign={handleWaitlistAssign}
          reservationSettings={reservationSettings ? {
            bookingMode: (reservationSettings.bookingMode as 'slot' | 'time') ?? 'slot',
            reservationDurationMinutes: reservationSettings.reservationDurationMinutes ?? 120,
          } : undefined}
        />
      </div>
        </div>

      </div>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />

      <Sheet open={!summaryCollapsed} onOpenChange={(open) => setSummaryCollapsed(!open)}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl">Tableau de bord</SheetTitle>
            <SheetDescription className="capitalize">
              {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
            </SheetDescription>
          </SheetHeader>
          {dashboardSummaryBlock}
        </SheetContent>
      </Sheet>

      {isMobile && (
        <DashboardBottomTabs
          activeTab={activeTab}
          onChange={(v: DashboardTabValue) => handleTabChange(v)}
          terraceEnabled={!!reservationSettings?.terraceEnabled}
        />
      )}
    </div>
  );
};

export default Dashboard;
